import { validateCreatureActionRequest, prepareCreatureAttack, resolveCreatureAttackRiders, beginCreatureMultiattack, mergeCreatureAttackStages, creatureCheckModifiers, creatureCheckAdvantageSources } from './CreatureAbilityResolver';
import { collectManualAttackRolls } from './AttackDicePreflight';
import { collectCreatureFaces } from './CreatureDice';
/**
 * ActionEconomyProcessor - Enhanced with Non-Attack Actions and Movement Validation
 *
 * D&D 5e Rule Reference: Action types, movement, and non-attack actions (PHB Ch.9)
 * date_of_change: 2025-09-19
 * purpose: Bridge ActionEconomyManager with discrete handlers for complete D&D 5e action coverage
 * guardrail: All actions must use canonical data only - no legacy transformations
 *
 * This processor ensures all actions pass through proper validation:
 * 1. Action economy validation via ActionEconomyManager
 * 2. Data validation via discrete handlers (Equipment, Spell, Ability, Combat, Species)
 * 3. Rules validation via CombatManager
 * 4. Logging via CombatLogManager
 * 5. Non-attack actions (Dodge, Disengage, Help, Hide, Ready)
 * 6. Movement validation with terrain and grapple checks
 *
 * ARCHITECTURAL REQUIREMENTS:
 * - All actions must be validated through this processor
 * - Canonical data only (CanonicalSchemas.ts)
 * - No DataService.js legacy transformations
 * - GM override hooks with audit logging
 * - Session snapshots are the only mutable state
 */

import ActionEconomyManager, { ACTION_TYPES } from '../ActionEconomyManager';
import { movementTracker } from '../MovementUtils';
import { SpellHandler } from '../../handlers/SpellHandler';
import { getStandardActionRule } from '../../rules/core/standardActions';
import CombatManager from '../CombatManager';
import CombatLogManager from '../CombatLogManager';
import RangeCalculator from '../RangeCalculator';
import { requestChoice, requestD20 } from '../DiceProvider';
// @date_of_change 2025-10-13 - POLICY: Remove default grid context injection
// rule_reference: AGENTS.md §9 NO LEGACY PATHS OR FALLBACKS
import {
  getCombatantActionBlockReason,
  resolveCombatantSpellData,
  resolveSpellLevel
} from './CombatantNormalizer';
import {
  buildGameMasterSpellResolutionRequest,
  classifySpellResolution
} from './SpellResolutionGate';
import { enrichSpellActionData } from './SpellActionReference';
import { isUseObjectAction } from './ObjectInteractionTargeting';
import { areCombatEnemies, isCombatantIncapacitated } from './CombatRelationships';
import {
  canTripSize,
  hasConditionImmunity,
  hasNamedCombatFeature,
  resolveSavingThrowModifier
} from './RogueCombatFeatures';

const HIDE_DC = 15;
const HIDE_CONTEXT_CHOICES = Object.freeze({
  'Heavily obscured and out of every enemy line of sight': 'heavily-obscured',
  'Behind three-quarters cover and out of every enemy line of sight': 'three-quarters-cover',
  'Behind total cover and out of every enemy line of sight': 'total-cover'
});
const HIDE_QUALIFYING_CONDITIONS = new Set(Object.values(HIDE_CONTEXT_CHOICES));

/**
 * ActionEconomyProcessor - Phase One Combat Action Validation and Execution
 * 
 * Integrates all combat systems for proper D&D 5e action economy enforcement
 */
export class ActionEconomyProcessor {
  
  /**
   * @param {Object} actionRequest - The action being attempted
   * @param {Object} combatState - Current combat state
   * @param {Object} gmOverride - Optional GM override settings
   * @returns {Object} - Action result with validation and execution details
   */
    // @date_of_change: 2025-10-09
  // Respect __actionEconomyConsumed to avoid double-consuming the action economy on confirm/retarget flows
  static async processAction(actionRequest, combatState, gmOverride = null) {
    validateCreatureActionRequest(actionRequest, combatState);
    const {
      actionType,
      actionName,
      attacker,
      targets = [],
      actionData = {},
      movementCost = 0
    } = actionRequest;

    // Work on a detached dice cursor; only the authoritative action commit
    // advances durable randomness. date_of_change: 2026-09-05
    const creatureRandomBefore = actionData.mechanics && combatState.random ? { ...combatState.random } : null;
    if (actionData.mechanics) combatState = { ...combatState, creatureResolution: true, creatureRolls: [], random: creatureRandomBefore ? { ...creatureRandomBefore } : undefined };

    const __economyConsumed = !!(actionRequest.__actionEconomyConsumed || actionData.__actionEconomyConsumed);

    this.assertProcessorActionRequest(actionRequest);

    // The canonical token snapshot is the only durable action-economy source.
    // Reconcile the singleton before validating every action so a movement or
    // bonus-action write cannot race the next request and leave Steady Aim (or
    // another action) judging stale in-memory state.
    // rule_reference: AGENTS.md — session snapshot is the single mutable source of truth.
    // date_of_change: 2026-07-18
    if (attacker?.combatFlags && typeof attacker.combatFlags === 'object') {
      const economyKeys = [
        'actionUsed', 'bonusActionUsed', 'reactionUsed', 'freeObjectUsed',
        'movementUsed', 'dashUsedThisTurn', 'movementLockedThisTurn', 'movementLockReason'
      ];
      if (economyKeys.some((key) => Object.prototype.hasOwnProperty.call(attacker.combatFlags, key))) {
        ActionEconomyManager.synchronizeCombatFlags(
          attacker.id,
          attacker.combatFlags,
          ActionEconomyProcessor.parseSpeedFeet(attacker?.speed)
        );
      }
    }

    console.log(`[PROCESS_ACTION_START] Processing action:`, {
      actionName,
      actionType,
      attackerName: attacker && attacker.name,
      targetCount: Array.isArray(targets) ? targets.length : 0,
      actionData
    });

    try {
      // Phase 1: Validate canonical data
      const dataValidation = await this.validateCanonicalData(attacker);
      if (!dataValidation.valid && !(gmOverride && gmOverride.ignoreDataValidation)) {
        return this.createFailureResult('Data validation failed', dataValidation.errors);
      }

      const actionBlockReason = getCombatantActionBlockReason(attacker);
      if (actionBlockReason) {
        return this.createFailureResult('Combatant cannot act', [actionBlockReason]);
      }

      // Phase 2: Validate action economy (unless already consumed)
      if (!__economyConsumed) {
        const economyValidation = this.validateActionEconomy(attacker.id, actionType, movementCost, attacker.speed);
        if (!economyValidation.valid && !(gmOverride && gmOverride.ignoreActionEconomy)) {
          return this.createFailureResult('Action economy violation', economyValidation.errors);
        }
      }

      // Phase 3: Validate through discrete handlers
      // @date_of_change: 2025-10-09 - Pass combatState to enable grid-aware range validation
      const handlerValidation = await this.validateThroughHandlers(actionType, actionData, attacker, targets, combatState);
      if (!handlerValidation.valid && !(gmOverride && gmOverride.ignoreHandlerValidation)) {
        return this.createFailureResult('Handler validation failed', handlerValidation.errors);
      }

      // Phase 4: Execute the action
      const executionResult = await this.executeAction(
        actionType,
        actionName,
        attacker,
        targets,
        actionData,
        combatState
      );

      // Closing an in-app roll request is not an action resolution. Return
      // before both economy mutation and audit logging so cancellation cannot
      // spend a resource or create a fake/null combat event.
      // date_of_change: 2026-07-18
      if (executionResult?.cancelled === true) {
        return executionResult;
      }
      if (executionResult.success && actionData.mechanics) executionResult.creatureRandom = {
        before: creatureRandomBefore, after: combatState.random ? { ...combatState.random } : null, rolls: combatState.creatureRolls
      };

      // Phase 5: Update action economy if successful (unless already consumed)
      if (executionResult.success && !__economyConsumed) {
        // @date_of_change: 2025-10-09 - Bracket-call to avoid react-hooks/rules-of-hooks false positive on method named 'useAction'
        // @date_of_change: 2025-10-10 - Pass normalized speed to ensure accurate remaining movement summaries
        const economyResult = ActionEconomyManager['useAction'](attacker.id, actionType, actionName, {
          movementCost,
          speed: ActionEconomyProcessor.parseSpeedFeet(attacker?.speed)
        });
        if (economyResult?.success) {
          ActionEconomyProcessor.attachActionEconomyStageUpdate(
            executionResult,
            actionType,
            economyResult.actionSummary
          );
        }
      }

      // The BattleRoom may defer the audit until its canonical token patch has
      // been accepted. This prevents a durable action log from claiming a
      // result that exists only in transient React/singleton state.
      // rule_reference: AGENTS.md - session snapshot is the single mutable source of truth.
      // date_of_change: 2026-07-18
      if (combatState?.deferAudit !== true) {
        this.logAction(executionResult, attacker, targets, gmOverride, actionRequest, combatState);
      }
      return executionResult;

    } catch (error) {
      const errorResult = this.createErrorResult(error, attacker, actionRequest);
      if (combatState?.deferAudit !== true) {
        this.logAction(errorResult, attacker, targets, gmOverride, actionRequest, combatState);
      }
      return errorResult;
    }
  }

  static assertProcessorActionRequest(actionRequest = {}) {
    const attacker = actionRequest?.attacker;
    const actionData = actionRequest?.actionData;
    const expectedActivations = {
      [ACTION_TYPES.ACTION]: 'action',
      [ACTION_TYPES.BONUS_ACTION]: 'bonus action',
      [ACTION_TYPES.REACTION]: 'reaction',
      [ACTION_TYPES.MOVEMENT]: 'movement',
      [ACTION_TYPES.FREE_OBJECT]: 'free object',
      [ACTION_TYPES.FREE_ACTION]: 'free'
    };
    const routes = ['attack', 'spell', 'ability', 'standard'];

    if (!attacker || typeof attacker !== 'object') {
      throw new Error('actionRequest.attacker must be the canonical combatant snapshot.');
    }
    if (typeof actionRequest.attackerId !== 'string' || !actionRequest.attackerId.trim()) {
      throw new Error('actionRequest.attackerId must be a non-empty string.');
    }
    if (attacker.id !== actionRequest.attackerId) {
      throw new Error('actionRequest.attacker.id must exactly match actionRequest.attackerId.');
    }
    if (!actionData || typeof actionData !== 'object') {
      throw new Error('actionRequest.actionData must be a structured action object.');
    }
    if (actionRequest.actionName !== actionData.name) {
      throw new Error('actionRequest.actionName must exactly match actionRequest.actionData.name.');
    }
    if (!routes.includes(actionData.executionRoute)) {
      throw new Error('actionRequest.actionData.executionRoute must be attack, spell, ability, or standard.');
    }
    if (!Object.prototype.hasOwnProperty.call(expectedActivations, actionRequest.actionType)) {
      throw new Error('actionRequest.actionType is not a supported action-economy value.');
    }
    if (actionData.activationType !== expectedActivations[actionRequest.actionType]) {
      throw new Error(`actionRequest.actionData.activationType must be ${expectedActivations[actionRequest.actionType]} for ${actionRequest.actionType}.`);
    }
  }

  /**
   * Mirror spent action economy onto the canonical token stage update.
   * ActionEconomyManager remains the rules calculator/event source, but token
   * combatFlags are the persisted turn snapshot consumed by ActionTracker,
   * targeting previews, service refreshes, and reconnects.
   * date_of_change: 2026-07-15
   */
  static attachActionEconomyStageUpdate(executionResult, actionType, actionSummary = null) {
    if (!executionResult || typeof executionResult !== 'object') return executionResult;

    const combatFlags = {};
    switch (actionType) {
      case ACTION_TYPES.ACTION:
        combatFlags.actionUsed = true;
        break;
      case ACTION_TYPES.BONUS_ACTION:
        combatFlags.bonusActionUsed = true;
        break;
      case ACTION_TYPES.REACTION:
        combatFlags.reactionUsed = true;
        break;
      case ACTION_TYPES.FREE_OBJECT:
        combatFlags.freeObjectUsed = true;
        break;
      case ACTION_TYPES.MOVEMENT: {
        const movementUsed = Number(actionSummary?.movement?.used);
        if (Number.isFinite(movementUsed)) combatFlags.movementUsed = Math.max(0, movementUsed);
        break;
      }
      default:
        break;
    }
    // Rehydrate Dash with movementUsed so reconnects retain the expanded budget.
    // date_of_change: 2026-07-18
    if (executionResult.dashUsed === true) {
      combatFlags.dashUsedThisTurn = true;
    }

    if (Object.keys(combatFlags).length === 0) return executionResult;

    const stageUpdates = executionResult.combatStageUpdates || {};
    const attackerUpdate = stageUpdates.attacker || {};
    executionResult.combatStageUpdates = {
      ...stageUpdates,
      attacker: {
        ...attackerUpdate,
        combatFlags: {
          ...(attackerUpdate.combatFlags || {}),
          ...combatFlags
        }
      }
    };
    return executionResult;
  }

  /**
   * Validate that attacker data conforms to CanonicalSchemas.ts
   */
  static async validateCanonicalData(attacker) {
    const errors = [];
    const warnings = [];
    const finite = (value) => typeof value === 'number' && Number.isFinite(value);
    const requireFinite = (value, path, minimum = null) => {
      if (!finite(value) || (minimum != null && value < minimum)) {
        errors.push(`${path} must be ${minimum == null ? 'a finite number' : `a finite number >= ${minimum}`}`);
      }
    };

    if (!attacker || typeof attacker !== 'object') {
      return { valid: false, errors: ['attacker must be a canonical combatant object'], warnings };
    }
    if (typeof attacker.id !== 'string' || !attacker.id.trim()) errors.push('attacker.id must be a non-empty string');
    if (typeof attacker.name !== 'string' || !attacker.name.trim()) errors.push('attacker.name must be a non-empty string');
    requireFinite(attacker.hp, 'attacker.hp', 0);
    requireFinite(attacker.maxHp, 'attacker.maxHp', 1);
    if (finite(attacker.hp) && finite(attacker.maxHp) && attacker.hp > attacker.maxHp) {
      errors.push('attacker.hp must not exceed attacker.maxHp');
    }
    requireFinite(attacker.ac, 'attacker.ac', 0);
    requireFinite(attacker.initiative, 'attacker.initiative');
    requireFinite(attacker.speed, 'attacker.speed', 0);

    const abilityNames = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
    if (!attacker.abilities || typeof attacker.abilities !== 'object' || Array.isArray(attacker.abilities)) {
      errors.push('attacker.abilities must be an object with canonical long-form ability names');
    } else {
      abilityNames.forEach((ability) => requireFinite(attacker.abilities[ability], `attacker.abilities.${ability}`));
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Normalize a speed value into numeric feet.
   * @date_of_change: 2025-10-10
   * @rule_reference: PHB movement measured in feet per turn; inputs may be number, string ("30 ft"), or object ({walk:30}).
   */
  static parseSpeedFeet(spd) {
    if (typeof spd === 'number' && Number.isFinite(spd) && spd >= 0) return spd;

    const serializedInput = (() => {
      try {
        return JSON.stringify(spd);
      } catch (serializationError) {
        return String(spd);
      }
    })();

    // @date_of_change: 2025-10-13 - Enforce explicit speed supply (rule_reference: AGENTS.md §9 NO DEFAULT INJECTION)
    throw new Error(
      `[ActionEconomyProcessor] Invalid or missing speed input. Received: ${serializedInput}. ` +
      'Provide canonical numeric speed (feet) per Character_Monster_Data_Architecture.md.'
    );
  }

  /**
   * Validate action economy through ActionEconomyManager
   */
  static validateActionEconomy(attackerId, actionType, movementCost, speed) {
    const combatantState = ActionEconomyManager.getCombatantState(attackerId);
    
    const errors = [];
    
    // date_of_change: 2025-10-10 - Normalize speed to numeric feet before validation
    // rule_reference: D&D 5e movement is measured in feet per turn (typically 30 ft base walk).
    const speedFeet = ActionEconomyProcessor.parseSpeedFeet(speed);
    
    // Lightweight debug to verify normalization without spamming logs
    if (typeof speed !== 'number') {
      console.log('[ActionEconomyProcessor] normalize speed ->', { input: speed, speedFeet });
    }

    if (!combatantState.canUseAction(actionType, movementCost, speedFeet)) {
      switch (actionType) {
        case ACTION_TYPES.ACTION:
          errors.push('Main action already used this turn');
          break;
        case ACTION_TYPES.BONUS_ACTION:
          errors.push('Bonus action already used this turn');
          break;
        case ACTION_TYPES.MOVEMENT:
          {
            // date_of_change: 2026-06-07
            // WHAT: Report movement remaining from the same Dash-aware summary used for validation.
            // WHY: Base speed minus spent movement under-reports availability after Dash and makes
            // otherwise valid movement look blocked in UI/debug output.
            const movementSummary = combatantState.getActionSummary(speedFeet)?.movement;
            const remaining = Math.max(0, Number(movementSummary?.available) || 0);
            errors.push(`Insufficient movement: ${remaining} feet remaining`);
          }
          break;
        case ACTION_TYPES.REACTION:
          errors.push('Reaction already used this round');
          break;
        case ACTION_TYPES.FREE_OBJECT:
          errors.push('Free object interaction already used this turn');
          break;
        default:
          // @date_of_change 2025-09-14 - Lint fix: add default case without changing behavior
          // Unknown action types should be validated earlier in processAction; we do not add an error here
          break;
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate through appropriate discrete handlers
   * @date_of_change: 2025-10-09 - Pass combatState grid options to range validation to prevent false out-of-range
   * @rule_reference: D&D 5e grid distance; measure in feet per cell, not raw pixels
   */
  static async validateThroughHandlers(actionType, actionData, attacker, targets, combatState) {
    const errors = [];

    try {
      // @date_of_change 2025-09-17 - CRITICAL FIX: Skip range validation for AoE abilities
      // AoE abilities like Fire Breath originate from the caster and don't need individual target range checks
      // The AoE area validation is handled by the BattleRoom's AoE targeting system
      // @date_of_change 2025-10-13 - POLICY: AoE detection must be structured (no description parsing)
      const isAoEAbility = !!(
        actionData.areaOfEffect && typeof actionData.areaOfEffect === 'object'
      );

      // All targeted combat actions need range validation (except AoE abilities)
      // @date_of_change 2025-09-13 - CRITICAL FIX: Use validateAttack instead of non-existent isWithinRange
      // @date_of_change 2025-10-09 - Provide grid options (feetPerCell, gridSize) from combatState
      // @date_of_change 2025-10-14 - Validate even when top-level actionData.range is absent (use structured fields)
      const isSelfTargetAction = actionData?.targetRequirement === 'self';
      const isObjectInteraction = actionData?.targetRequirement === 'object';

      if (targets.length > 0 && !isAoEAbility && !isSelfTargetAction) {
        // @date_of_change 2025-10-13 - POLICY: Require explicit grid context (no defaults)
        const feetPerCell = Number(combatState?.feetPerCell);
        const gridSize = Number(combatState?.gridSize);
        const gridType = combatState?.gridType;
        if (!gridType || !Number.isFinite(feetPerCell) || !Number.isFinite(gridSize)) {
          errors.push('Missing grid context (gridType/feetPerCell/gridSize)');
          return { valid: false, errors };
        }
        const battleState = {
          gridSize,
          feetPerCell,
          obstacles: combatState?.obstacles || []
        };
        const gridOptions = {
          gridType,
          feetPerCell,
          gridSize
        };

        // @date_of_change: 2025-10-10 - Sanitize grid coordinates that may arrive as numeric strings
        const sanitizeGridToken = (t) => {
          if (!t) return t;
          const gxRaw = t.gridX;
          const gyRaw = t.gridY;
          const gx = (typeof gxRaw === 'string') ? (gxRaw.trim().toLowerCase() === 'off grid' ? NaN : Number(gxRaw)) : gxRaw;
          const gy = (typeof gyRaw === 'string') ? (gyRaw.trim().toLowerCase() === 'off grid' ? NaN : Number(gyRaw)) : gyRaw;
          return {
            ...t,
            gridX: Number.isFinite(gx) ? gx : t.gridX,
            gridY: Number.isFinite(gy) ? gy : t.gridY,
            size: t.size || 'Medium'
          };
        };

        // Aggressive selects a visible hostile creature as a direction anchor,
        // not as an attack target. Validate its explicit sight requirement and
        // leave distance unrestricted; the GM confirms that the subsequent
        // drag remains toward that creature.
        // rule_reference: SRD 5.1 Orc — Aggressive; date_of_change: 2026-09-02
        if (actionData?.executionRoute === 'ability' && actionData?.featureKey === 'aggressive') {
          for (const tgt of targets) {
            const sanitizedAttacker = sanitizeGridToken(attacker);
            const sanitizedTarget = sanitizeGridToken(tgt);
            const hasLineOfSight = RangeCalculator.hasLineOfSight(
              sanitizedAttacker,
              sanitizedTarget,
              battleState.obstacles,
              gridOptions
            );
            if (!hasLineOfSight) {
              errors.push(`Target ${tgt.name} is not visible to ${attacker.name}; Aggressive requires a visible hostile creature`);
            }
          }
          return { valid: errors.length === 0, errors };
        }

        // Resolve spatial validation from explicit structured fields only.
        // date_of_change: 2026-08-21
        let attackTypeHint = null;
        if (typeof actionData?.attackType === 'string') {
          const t = actionData.attackType.toLowerCase();
          if (t === 'melee' || t === 'ranged') attackTypeHint = t;
        }
        if (!attackTypeHint && typeof actionData?.attack?.type === 'string') {
          const tt = actionData.attack.type.toLowerCase();
          if (tt.includes('melee')) attackTypeHint = 'melee';
          else if (tt.includes('ranged')) attackTypeHint = 'ranged';
        }
        if (!attackTypeHint && actionData?.executionRoute === 'spell' && actionData?.targetRequirement === 'creature') {
          attackTypeHint = 'ranged';
        }
        // A token-backed object interaction is not an attack, but it still has a
        // spatial requirement. Give it an explicit melee-style interaction reach
        // so the common range validator can enforce adjacency without trying to
        // infer an attack type from a utility action.
        if (isObjectInteraction) {
          attackTypeHint = 'melee';
        }

        // If this is a spell and resolver can provide numeric range, use it for validation
        let actionForRange = actionData;
        if (isObjectInteraction) {
          const interactionReach = Number(actionData?.interactionRangeFeet ?? actionData?.rangeFeet);
          if (!Number.isFinite(interactionReach) || interactionReach < 0) {
            errors.push('actionData.rangeFeet must be provided for a targeted object interaction');
            return { valid: false, errors };
          }
          actionForRange = {
            ...actionData,
            reach: interactionReach,
            interactionRangeFeet: interactionReach
          };
        }
        for (const tgt of targets) {
          // Skip range validation if target is the attacker (self-targeting abilities)
          if (tgt.id === attacker.id) {
            console.log(`[ActionEconomyProcessor] Skipping range validation for self-targeting: ${tgt.name}`);
            continue;
          }

          const sanitizedAttacker = sanitizeGridToken(attacker);
          const sanitizedTarget = sanitizeGridToken(tgt);

          const rangeCheck = RangeCalculator.validateAttack(
            sanitizedAttacker,
            sanitizedTarget,
            actionForRange,
            battleState,
            { ...gridOptions, attackType: attackTypeHint }
          );
          if (!rangeCheck.valid) {
            // Extra diagnostics for melee misclassification
            try {
              const diag = RangeCalculator.getEdgeDistanceFeet?.(sanitizedAttacker, sanitizedTarget, gridOptions);
              console.error(`🚨 [ActionEconomyProcessor] POPUP_SOURCE_2: Target ${tgt.name} out of range`, {
                target: sanitizedTarget,
                actionData,
                rangeCheck,
                attacker: sanitizedAttacker,
                gridOptions,
                diag,
                stackTrace: new Error().stack
              });
            } catch (diagErr) {
              console.error('[ActionEconomyProcessor] Range diag failed:', diagErr);
            }
            errors.push(`Target ${tgt.name} out of range: ${rangeCheck.reasons?.join(', ') || 'Unknown range error'}`);
          }
        }
      } else if (isAoEAbility) {
        console.log(`[ActionEconomyProcessor] Skipping range validation for AoE ability: ${actionData.name}`);
      }

    } catch (error) {
      errors.push(`Handler validation error: ${error.message}`);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Execute the validated action through CombatManager
   */
  static async executeAction(actionType, actionName, attacker, targets, actionData, combatState) {
    try {
      let result;
      switch (actionData.executionRoute) {
        case 'attack':
          result = await this.executeAttack(attacker, targets, actionData, combatState);
          break;
        case 'spell':
          result = await this.executeSpellCast(attacker, targets, actionData, combatState);
          break;
        case 'ability':
          result = await this.executeAbilityRoute(actionType, actionName, attacker, targets, actionData, combatState);
          break;
        case 'standard':
          result = await this.executeStandardRoute(actionType, actionName, attacker, targets, actionData, combatState);
          break;
        default:
          throw new Error(`Unsupported actionData.executionRoute: ${String(actionData.executionRoute)}`);
      }
      if (result && typeof result === 'object') result.actionType = actionType;
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message,
        actionType,
        actionName
      };
    }
  }

  /**
   * Dispatch a class/monster ability only from explicit structured mechanics.
   * Feature names and descriptions are presentation and never select a resolver.
   * rule_reference: AGENTS.md no runtime inference from prose or names.
   * date_of_change: 2026-08-21
   */
  static async executeAbilityRoute(actionType, actionName, attacker, targets, actionData, combatState) {
    if (actionData.interactionIntent) {
      return this.executeDeclaredInteraction(actionName, actionData, actionType);
    }

    switch (actionData.featureKey) {
      case 'rage':
        return this.executeRage(attacker, actionData, combatState);
      case 'lay_on_hands':
        return this.executeLayOnHands(attacker, targets, actionData);
      case 'second_wind':
        return this.executeSecondWind(attacker, actionData);
      case 'steady_aim':
        return this.executeSteadyAim(attacker, actionData, combatState);
      case 'aggressive':
        return this.executeAggressive(attacker, targets, actionData);
      default:
        break;
    }

    if (ActionEconomyProcessor.hasStructuredSavingThrow(actionData)) {
      return this.executeSavingThrowAbility(attacker, targets, actionData, combatState);
    }
    if (ActionEconomyProcessor.hasStructuredActionCheck(actionData)) {
      return this.executeActionCheck(attacker, actionData, combatState);
    }

    throw new Error(
      `${actionName} requires a registered featureKey, a structured save/check, or an explicit interactionIntent.`
    );
  }

  /**
   * Aggressive grants movement toward one visible hostile creature. BattleRoom
   * owns path selection. Only the authoritative movement transaction may spend
   * its bonus action; selecting a target is not resolution.
   * rule_reference: SRD 5.1 Orc - Aggressive; date_of_change: 2026-09-05
   */
  static executeAggressive(attacker, targets, actionData) {
    const target = Array.isArray(targets) ? targets[0] : null;
    if (!target?.id) {
      // Dismissing the target picker is not an attempted ability use. Match
      // other in-app cancellation contracts so no bonus action or combat event
      // is committed. rule_reference: SRD 5.1 Orc - Aggressive;
      // date_of_change: 2026-09-06
      return {
        success: false,
        cancelled: true,
        code: 'ACTION_CANCELLED',
        actionType: ACTION_TYPES.BONUS_ACTION,
        actionName: actionData?.name || 'Aggressive',
        effect: 'Aggressive target selection was cancelled; no bonus action was spent.'
      };
    }

    const maxFeet = this.parseSpeedFeet(attacker?.speed);
    if (!Number.isFinite(maxFeet) || maxFeet <= 0) {
      throw new Error('Aggressive requires canonical attacker.speed greater than 0 feet.');
    }

    return {
      success: false,
      code: 'MOVEMENT_PATH_REQUIRED',
      actionName: actionData?.name || 'Aggressive',
      error: `Choose a path toward ${target.name || target.id} using the BattleRoom token drag. The bonus action is spent only when that movement is committed.`,
      requiresMovementPath: true
    };
  }

  /**
   * Standard actions dispatch by canonical key, never by localized/display name.
   * date_of_change: 2026-08-21
   */
  static async executeStandardRoute(actionType, actionName, attacker, targets, actionData, combatState) {
    switch (actionData.standardActionKey) {
      case 'dash':
      case 'bonus-action-dash':
        return this.executeDash(attacker, actionData, combatState);
      case 'disengage':
      case 'bonus-action-disengage':
        return this.executeDisengage(attacker, actionData, combatState);
      case 'dodge':
        return this.executeDodge(attacker, actionData, combatState);
      case 'help':
        return this.executeHelp(attacker, targets, actionData, combatState);
      case 'hide':
      case 'bonus-action-hide':
        return this.executeHide(attacker, actionData, combatState);
      case 'ready':
        return this.executeReady(attacker, actionData, combatState);
      case 'search':
        return this.executeSearch(attacker, actionData, combatState);
      case 'use-object':
        return this.executeUseObject(attacker, actionData, combatState);
      case 'move':
        return this.executeMovement(attacker, actionData, combatState);
      case 'speak':
        return this.executeSpeech(actionName, actionData, actionType);
      case 'drop-item':
      case 'draw-weapon':
        return this.executeDeclaredInteraction(actionName, actionData, actionType);
      default:
        throw new Error('actionData.standardActionKey must identify a configured standard action.');
    }
  }

  static executeSpeech(actionName, actionData, actionType) {
    const speech = actionData?.speech;
    if (!speech?.text || !String(speech.text).trim()) {
      return {
        success: false,
        actionType,
        actionName,
        code: 'INTERACTION_INTENT_REQUIRED',
        error: 'Enter what the combatant says and who should hear it.',
        requiresInteractionIntent: true
      };
    }
    return {
      success: true,
      actionType,
      actionName,
      effect: `Speech recorded${speech.targetName ? ` for ${speech.targetName}` : ''}`,
      resolutionStatus: 'recorded',
      speech: { ...speech, text: String(speech.text).trim() },
      actionData
    };
  }

  /**
   * Resolve a strict saving throw for an attack rider. Missing canonical
   * ability/save data blocks the entire action before any token patch commits.
   * rule_reference: 2024 PHB — Saving Throws; AGENTS.md strict validation.
   * date_of_change: 2026-07-18
   */
  static async resolveAttackRiderSavingThrow(target, ability, dc, combatState = {}) {
    const modifierResult = resolveSavingThrowModifier(target, ability);
    if (!modifierResult.valid) return { success: false, error: modifierResult.error };

    const conditionKeys = new Set((Array.isArray(target?.conditions) ? target.conditions : [])
      .map((condition) => String(condition?.name ?? condition).trim().toLowerCase()));
    const autoFail = ['strength', 'dexterity'].includes(ability)
      && ['paralyzed', 'stunned', 'unconscious'].some((condition) => conditionKeys.has(condition));
    let d20Roll = 1;
    if (!autoFail) {
      if (CombatManager.shouldUseManualRolls(target)) {
        const rollRequester = typeof combatState?.requestD20 === 'function'
          ? combatState.requestD20
          : requestD20;
        const rolls = await rollRequester({
          numDice: 1,
          title: `${target?.name || 'Target'} — ${ability} saving throw`,
          prompt: `Enter the raw d20 face for the DC ${dc} ${ability} saving throw.`
        });
        if (!Array.isArray(rolls) || rolls.length === 0) {
          return {
            success: false,
            cancelled: true,
            code: 'REACTION_OR_RIDER_ROLL_CANCELLED',
            error: `${target?.name || 'Target'} saving throw was canceled; no combat state was committed.`
          };
        }
        d20Roll = Number(rolls[0]);
        if (!Number.isInteger(d20Roll) || d20Roll < 1 || d20Roll > 20) {
          return { success: false, error: 'Cunning Strike saving throw requires one valid d20 result.' };
        }
      } else {
        d20Roll = combatState.creatureResolution
          ? (await collectCreatureFaces(target, combatState, 20, 1, async () => []))[0]
          : CombatManager.rollD20({}).value;
      }
    }

    const total = autoFail ? 1 : d20Roll + modifierResult.modifier;
    return {
      success: true,
      saveSuccess: !autoFail && total >= dc,
      savingThrow: {
        ability,
        d20Roll,
        modifier: modifierResult.modifier,
        total,
        dc,
        autoFail
      }
    };
  }

  /**
   * Resolve the selected Cunning Strike rider after a hit has actually dealt
   * Sneak Attack damage. The returned token patch is committed with the attack.
   * rule_reference: 2024 PHB Rogue — Cunning Strike.
   * date_of_change: 2026-07-18
   */
  static async resolveCunningStrike(attacker, target, attackResult, combatState = {}) {
    const rider = attackResult?.cunningStrike;
    if (!rider || rider.pendingResolution !== true) return { success: true, targetStageUpdate: null };

    if (rider.effect === 'withdraw') {
      const speed = ActionEconomyProcessor.parseSpeedFeet(attacker?.speed);
      if (!Number.isFinite(speed) || speed <= 0) {
        return { success: false, error: 'Cunning Strike Withdraw requires canonical attacker.speed.' };
      }
      attackResult.cunningStrike = {
        ...rider,
        pendingResolution: false,
        maxFeet: speed / 2,
        noOpportunityAttacksFromTarget: true
      };
      return { success: true, targetStageUpdate: null };
    }

    if (rider.effect === 'trip') {
      const sizeResult = canTripSize(target?.size);
      if (!sizeResult.valid) return { success: false, error: sizeResult.error };
      if (!sizeResult.allowed) {
        return { success: false, error: `Cunning Strike Trip cannot affect a ${target.size} target; target must be Large or smaller.` };
      }
    }

    if (rider.effect === 'poison' && hasConditionImmunity(target, 'poisoned')) {
      attackResult.cunningStrike = {
        ...rider,
        pendingResolution: false,
        immune: true,
        conditionApplied: false
      };
      return { success: true, targetStageUpdate: null };
    }

    const saveResult = await ActionEconomyProcessor.resolveAttackRiderSavingThrow(
      target,
      rider.saveAbility,
      rider.saveDC,
      combatState
    );
    if (!saveResult.success) return saveResult;

    const condition = rider.effect === 'poison' ? 'Poisoned' : 'Prone';
    const failedSave = !saveResult.saveSuccess;
    attackResult.cunningStrike = {
      ...rider,
      pendingResolution: false,
      savingThrow: saveResult.savingThrow,
      saveSuccess: saveResult.saveSuccess,
      conditionApplied: failedSave ? condition : false
    };
    if (!failedSave) return { success: true, targetStageUpdate: null };

    if (condition === 'Poisoned') {
      return {
        success: true,
        targetStageUpdate: {
          id: target.id,
          conditions: ['Poisoned'],
          statusEffects: { poisoned: true },
          statusEffectsMeta: {
            poisoned: {
              sourceId: attacker.id,
              sourceName: 'Cunning Strike: Poison',
              condition: 'Poisoned',
              roundsRemaining: 10,
              saveDC: rider.saveDC
            }
          }
        }
      };
    }
    return { success: true, targetStageUpdate: { id: target.id, conditions: ['Prone'] } };
  }

  /**
   * Offer Uncanny Dodge only after a visible attacker hits. Accepting stages
   * the reaction and reduced HP together; canceling blocks the transaction.
   * rule_reference: 2024 PHB Rogue — Uncanny Dodge.
   * date_of_change: 2026-07-18
   */
  static async resolveUncannyDodge(attacker, target, attackResult, combatState = {}) {
    const damage = Number(attackResult?.damage?.total);
    const eligibleHit = attackResult?.attackRoll?.isHit === true && Number.isFinite(damage) && damage > 0;
    if (!eligibleHit || !hasNamedCombatFeature(target, 'Uncanny Dodge') || attackResult.targetCanSeeAttacker !== true) {
      return { success: true, targetStageUpdate: null };
    }

    if (!target?.combatFlags || !Object.prototype.hasOwnProperty.call(target.combatFlags, 'reactionUsed')) {
      return {
        success: false,
        error: `${target.name || target.id} cannot resolve Uncanny Dodge: canonical combatFlags.reactionUsed is missing.`
      };
    }
    if (target.combatFlags.reactionUsed === true) return { success: true, targetStageUpdate: null };

    const choiceRequester = typeof combatState?.requestChoice === 'function'
      ? combatState.requestChoice
      : requestChoice;
    const useLabel = `Use Uncanny Dodge — reduce ${damage} damage to ${Math.floor(damage / 2)}`;
    const fullLabel = `Take full damage — ${damage}`;
    const choice = await choiceRequester({
      title: `${target.name || 'Target'} — Uncanny Dodge`,
      prompt: `${attacker.name || 'The attacker'} hit ${target.name || 'the target'}. Spend the reaction?`,
      options: [useLabel, fullLabel]
    });
    if (choice == null) {
      return {
        success: false,
        cancelled: true,
        code: 'REACTION_DECISION_REQUIRED',
        error: 'Uncanny Dodge decision was canceled; no attack state was committed.'
      };
    }
    if (choice === fullLabel) return { success: true, targetStageUpdate: null };
    if (choice !== useLabel) {
      return { success: false, error: `Invalid Uncanny Dodge decision: ${choice}` };
    }

    const reducedDamage = Math.floor(damage / 2);
    attackResult.damage = {
      ...attackResult.damage,
      total: reducedDamage,
      beforeUncannyDodge: damage,
      uncannyDodgeApplied: true
    };
    attackResult.updatedTarget = CombatManager.applyDamage(target, reducedDamage);
    attackResult.uncannyDodge = {
      used: true,
      reactionSpent: true,
      originalDamage: damage,
      reducedDamage
    };
    return {
      success: true,
      targetStageUpdate: { id: target.id, combatFlags: { reactionUsed: true } }
    };
  }

  static async executeAttack(attacker, targets, actionData, combatState = {}) {
    if (actionData?.mechanics?.kind === 'multiattack') return beginCreatureMultiattack(attacker, actionData, combatState);
    const resolvedTargets = Array.isArray(targets) ? targets.filter(Boolean) : [];
    if (resolvedTargets.length === 0) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: actionData?.name || 'Attack',
        error: 'Attack requires at least one target'
      };
    }

    // date_of_change: 2026-07-12 - Manual roll bridge for Action Targeting panel
    // rule_reference: AGENTS.md (GM hooks; avoid native window.prompt UX; deterministic replay)
    // When the Action Targeting UI pre-collects the attack d20 / weapon damage dice inline
    // (single-target case), it stashes them on actionData as __manualRolls (attack d20 face(s))
    // and __manualRollQueue (ordered Bless/Bane + damage dice faces). We forward them into
    // CombatManager.executeAttack's options so the engine consumes the player-entered values
    // instead of prompting again. Only meaningful for a single resolved target; multi-target
    // attacks fall back to the existing per-roll prompt/auto-roll behavior.
    const manualRollOptions = (resolvedTargets.length === 1 && Array.isArray(actionData?.__manualRolls))
      ? {
        manualRolls: actionData.__manualRolls,
        manualRollQueue: Array.isArray(actionData.__manualRollQueue) ? [...actionData.__manualRollQueue] : []
      }
      : {};

    const attackResults = [];
    const targetStageUpdates = new Map();
    let sneakAttackResolved = false;
    const mergeTargetStageUpdate = (target, update) => {
      if (!target?.id || !update || typeof update !== 'object') return;
      const previous = targetStageUpdates.get(target.id) || { id: target.id };
      targetStageUpdates.set(target.id, {
        ...previous,
        ...update,
        ...(previous.combatFlags || update.combatFlags ? {
          combatFlags: { ...(previous.combatFlags || {}), ...(update.combatFlags || {}) }
        } : {}),
        ...(previous.statusEffects || update.statusEffects ? {
          statusEffects: { ...(previous.statusEffects || {}), ...(update.statusEffects || {}) }
        } : {}),
        ...(previous.statusEffectsMeta || update.statusEffectsMeta ? {
          statusEffectsMeta: { ...(previous.statusEffectsMeta || {}), ...(update.statusEffectsMeta || {}) }
        } : {}),
        ...(previous.conditions || update.conditions ? {
          conditions: Array.from(new Set([...(previous.conditions || []), ...(update.conditions || [])]))
        } : {})
      });
    };

    for (const target of resolvedTargets) {
      const preparedCreatureAction = prepareCreatureAttack(attacker, target, actionData, combatState);
      const creatureRolls = actionData.mechanics ? await collectManualAttackRolls(attacker, target, preparedCreatureAction, combatState) : null;
      const attackResult = CombatManager.executeAttack(
        attacker,
        target,
        preparedCreatureAction,
        combatState || {},
        { ...manualRollOptions, ...(creatureRolls || {}), sneakAttackAlreadyUsed: sneakAttackResolved }
      );

      if (attackResult?.success === false) {
        return {
          success: false,
          actionType: ACTION_TYPES.ACTION,
          actionName: actionData?.name || 'Attack',
          error: attackResult.message || attackResult.error || 'Attack execution failed'
        };
      }

      if (attackResult?.sneakAttack) {
        sneakAttackResolved = true;
      }

      const cunningResolution = await ActionEconomyProcessor.resolveCunningStrike(
        attacker,
        target,
        attackResult,
        combatState
      );
      if (!cunningResolution.success) {
        return {
          ...cunningResolution,
          actionType: ACTION_TYPES.ACTION,
          actionName: actionData?.name || 'Attack'
        };
      }
      mergeTargetStageUpdate(target, cunningResolution.targetStageUpdate);

      const uncannyResolution = await ActionEconomyProcessor.resolveUncannyDodge(
        attacker,
        target,
        attackResult,
        combatState
      );
      if (!uncannyResolution.success) {
        return {
          ...uncannyResolution,
          actionType: ACTION_TYPES.ACTION,
          actionName: actionData?.name || 'Attack'
        };
      }
      mergeTargetStageUpdate(target, uncannyResolution.targetStageUpdate);

      const creatureStage = await resolveCreatureAttackRiders(attacker, target, preparedCreatureAction, attackResult, combatState, ActionEconomyProcessor.resolveAttackRiderSavingThrow);
      mergeTargetStageUpdate(target, creatureStage);

      attackResults.push({
        ...attackResult,
        target: attackResult?.target || target?.name,
        targetId: attackResult?.targetId || target?.id,
        hit: attackResult?.attackRoll?.isHit ?? attackResult?.hit ?? false,
        targetAC: attackResult?.attackRoll?.targetAC ?? target?.ac ?? target?.armorClass ?? null
      });
    }

    const successfulResults = attackResults.filter((entry) => entry?.success !== false);
    const normalizedConditions = (Array.isArray(attacker?.conditions) ? attacker.conditions : [])
      .map((condition) => String(condition?.name ?? condition).trim().toLowerCase().replace(/[\s_-]+/g, ''));
    const wasHidden = normalizedConditions.includes('hidden')
      || (Array.isArray(attacker?.combatFlags?.hiddenFrom) && attacker.combatFlags.hiddenFrom.length > 0);
    const usedSteadyAim = attacker?.combatFlags?.steadyAimActive === true;
    const hiddenStealthCheck = attacker?.combatFlags?.hiddenStealthCheck || null;
    const roundNow = Number(combatState?.round ?? combatState?.currentRound ?? CombatLogManager?.currentRound) || 1;
    const turnNow = Number(combatState?.turn ?? combatState?.currentTurn ?? CombatLogManager?.currentTurn) || 0;
    const usedSneakAttack = attackResults.some((entry) => entry?.sneakAttack);
    const withdrawResult = attackResults.find((entry) => entry?.cunningStrike?.effect === 'withdraw');
    let attackerStageUpdate = (wasHidden || usedSteadyAim || usedSneakAttack || withdrawResult)
      ? {
          ...(wasHidden ? { conditionsToRemove: ['Hidden'] } : {}),
          combatFlags: {
            ...(wasHidden ? { hiddenFrom: [], hiddenStealthCheck: null } : {}),
            ...(usedSteadyAim ? { steadyAimActive: false } : {}),
            ...(usedSneakAttack ? {
              sneakAttackUsedRound: roundNow,
              sneakAttackUsedTurn: turnNow
            } : {}),
            ...(withdrawResult ? {
              cunningStrikeWithdraw: {
                maxFeet: withdrawResult.cunningStrike.maxFeet,
                sourceTargetId: withdrawResult.targetId,
                round: roundNow,
                turn: turnNow
              }
            } : {})
          }
        }
      : null;

    attackerStageUpdate = mergeCreatureAttackStages(attacker, actionData, combatState, attackResults, attackerStageUpdate);

    return {
      success: successfulResults.length > 0,
      actionType: ACTION_TYPES.ACTION,
      actionName: actionData?.name || 'Attack',
      results: attackResults,
      totalDamage: attackResults.reduce((sum, entry) => sum + (Number(entry?.damage?.total) || 0), 0),
      ...((attackerStageUpdate || targetStageUpdates.size > 0) ? {
        combatStageUpdates: {
          ...(attackerStageUpdate ? { attacker: attackerStageUpdate } : {}),
          ...(targetStageUpdates.size > 0 ? { targets: [...targetStageUpdates.values()] } : {})
        }
      } : {}),
      ...(hiddenStealthCheck ? { hiddenStealthCheck } : {})
    };
  }

  // D&D 5e rule reference: skill checks use d20 + the relevant ability and proficiency modifiers;
  // date_of_change: 2026-07-11 - Normalize non-attack check results for audit logging.
  static normalizeCheckRoll(checkResult) {
    if (!checkResult || typeof checkResult !== 'object') return null;

    const modifier = Number(checkResult.modifier ?? checkResult.skillBonus ?? 0);
    const total = Number(checkResult.total);
    const roll = Number(checkResult.d20Roll ?? checkResult.roll ?? checkResult.chosenValue);
    if (!Number.isFinite(modifier) || !Number.isFinite(total) || !Number.isFinite(roll)) {
      return null;
    }

    const modifierText = modifier >= 0 ? `+${modifier}` : `${modifier}`;
    return {
      ...checkResult,
      d20Roll: roll,
      modifier,
      total,
      formula: checkResult.formula || `1d20${modifierText}`,
      rolls: Array.isArray(checkResult.rolls) && checkResult.rolls.length > 0
        ? checkResult.rolls
        : [roll]
    };
  }

  static buildCheckAuditRoll(checkResult) {
    const normalized = ActionEconomyProcessor.normalizeCheckRoll(checkResult);
    if (!normalized) return [];
    return [{
      formula: normalized.formula,
      total: normalized.total,
      rolls: normalized.rolls,
      rollType: normalized.rollType,
      advantage: normalized.advantage,
      disadvantage: normalized.disadvantage
    }];
  }

  static normalizeActionCheck(actionData = {}) {
    const check = actionData.check || actionData.skillCheck || actionData.abilityCheck;
    if (!check || typeof check !== 'object') return null;

    const rawSkill = typeof check.skill === 'string' ? check.skill.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ') : '';
    const skillAliases = {
      'sleight of hand': 'sleightOfHand',
      'sleightofhand': 'sleightOfHand',
      'animal handling': 'animalHandling',
      'animalhandling': 'animalHandling'
    };
    const skill = rawSkill ? (skillAliases[rawSkill] || rawSkill.replace(/\s+/g, '')) : '';
    const ability = typeof check.ability === 'string' ? check.ability.trim() : '';
    const type = String(check.type || (skill ? 'skill' : 'ability')).trim().toLowerCase();
    if (type === 'skill' && !skill) return null;
    if (type === 'ability' && !ability) return null;
    if (type !== 'skill' && type !== 'ability') return null;

    return {
      ...check,
      type,
      ...(skill ? { skill } : {}),
      ...(ability ? { ability } : {})
    };
  }

  static hasStructuredActionCheck(actionData = {}) {
    return Boolean(ActionEconomyProcessor.normalizeActionCheck(actionData));
  }

  static async resolveCheckModifiers(attacker, modifiers = {}, label = 'check', combatState = {}) {
    const advantage = modifiers?.advantage === true;
    const disadvantage = modifiers?.disadvantage === true;
    const requiredRollCount = advantage !== disadvantage ? 2 : 1;
    const validateRolls = (rolls) => (
      Array.isArray(rolls)
      && rolls.length === requiredRollCount
      && rolls.every((roll) => Number.isInteger(Number(roll)) && Number(roll) >= 1 && Number(roll) <= 20)
    );

    if (Array.isArray(modifiers?.manualRolls) && modifiers.manualRolls.length > 0) {
      return validateRolls(modifiers.manualRolls)
        ? { success: true, modifiers: { ...modifiers, manualRolls: modifiers.manualRolls.map(Number) } }
        : { success: false, error: `Manual ${label} requires ${requiredRollCount} valid d20 result${requiredRollCount === 1 ? '' : 's'}.` };
    }

    if (!CombatManager.shouldUseManualRolls(attacker)) {
      if (combatState.creatureResolution) {
        const rolls = await collectCreatureFaces(attacker, combatState, 20, requiredRollCount, async () => []);
        return { success: true, modifiers: { ...modifiers, manualRolls: rolls } };
      }
      return { success: true, modifiers };
    }

    const rollRequester = typeof combatState?.requestD20 === 'function'
      ? combatState.requestD20
      : requestD20;
    let rolls;
    try {
      rolls = await rollRequester({
        numDice: requiredRollCount,
        title: `${attacker?.name || 'Combatant'} — ${label}`,
        prompt: requiredRollCount === 2
          ? `Enter both raw d20 faces (${advantage ? 'advantage' : 'disadvantage'}). VCS applies modifiers automatically.`
          : 'Enter the raw d20 face. VCS applies modifiers automatically.'
      });
    } catch (error) {
      return { success: false, error: `Manual ${label} could not be collected: ${error.message}` };
    }

    if (!Array.isArray(rolls) || rolls.length === 0) {
      return {
        success: false,
        cancelled: true,
        code: 'ACTION_CANCELLED',
        error: `${label} cancelled; no action was spent.`
      };
    }
    if (!validateRolls(rolls)) {
      return { success: false, error: `Manual ${label} requires ${requiredRollCount} valid d20 result${requiredRollCount === 1 ? '' : 's'}.` };
    }

    return {
      success: true,
      modifiers: { ...modifiers, manualRolls: rolls.map(Number) }
    };
  }

  static async executeActionCheck(attacker, actionData, combatState = {}) {
    const check = ActionEconomyProcessor.normalizeActionCheck(actionData);
    if (!check) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: actionData?.name || 'Action',
        error: 'Non-attack check requires structured check data with type and skill or ability'
      };
    }

    const requestedModifiers = {
      ...creatureCheckModifiers(attacker, check),
      ...(combatState?.modifiers || {}),
      ...(actionData?.options || {}),
      ...(Number.isFinite(Number(check.bonus)) ? { bonus: Number(check.bonus) } : {})
    };
    const checkLabel = check.type === 'skill' ? `${check.skill} check` : `${check.ability} ability check`;
    const modifierResolution = await ActionEconomyProcessor.resolveCheckModifiers(
      attacker,
      requestedModifiers,
      checkLabel,
      combatState
    );
    if (!modifierResolution.success) {
      return {
        ...modifierResolution,
        actionType: ACTION_TYPES.ACTION,
        actionName: actionData?.name || 'Action'
      };
    }
    const modifiers = modifierResolution.modifiers;
    const checkResult = check.type === 'skill'
      ? CombatManager.rollSkillCheck(attacker, check.skill, modifiers)
      : CombatManager.rollAbilityCheck(attacker, check.ability, modifiers);
    if (checkResult?.error) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: actionData?.name || 'Action',
        error: checkResult.error,
        checkError: checkResult.error,
        check: checkResult
      };
    }
    const checkRoll = ActionEconomyProcessor.normalizeCheckRoll(checkResult);
    if (!checkRoll) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: actionData?.name || 'Action',
        error: 'Non-attack check did not return a complete d20 result'
      };
    }

    const resolvedCheckLabel = check.type === 'skill' ? check.skill : `${check.ability} ability`;
    const dc = Number(check.dc);
    const hasDC = Number.isFinite(dc);
    return {
      success: true,
      actionType: ACTION_TYPES.ACTION,
      actionName: actionData?.name || 'Action',
      effect: `Made ${resolvedCheckLabel} check${hasDC ? ` vs DC ${dc}` : ''}`,
      standardAction: getStandardActionRule(actionData?.standardActionKey),
      check: checkResult,
      checkRoll,
      rolls: ActionEconomyProcessor.buildCheckAuditRoll(checkRoll),
      ...(hasDC ? { dc, checkSuccess: checkRoll.total >= dc } : {})
    };
  }

  static normalizeSavingThrowAbility(actionData = {}) {
    const explicitSave = actionData.save || actionData.savingThrow || null;
    const ability = String(
      explicitSave?.ability
      ?? actionData.saveType
      ?? actionData.saveAbility
      ?? ''
    ).trim().toLowerCase();
    const dc = Number(explicitSave?.dc ?? actionData.saveDC ?? actionData.dc);

    if (!ability || !Number.isFinite(dc)) {
      return null;
    }

    const normalizedDamage = Array.isArray(actionData.damage)
      ? actionData.damage.map((entry) => {
          if (!entry || typeof entry !== 'object') return entry;
          return {
            ...entry,
            // date_of_change: 2026-06-07
            // WHAT: Normalize save damage entries to the formula field CombatManager consumes.
            // WHY: Ability catalog entries usually carry dice strings; without this bridge,
            // save-based abilities can roll malformed formulas or no meaningful damage.
            formula: entry.formula ?? entry.dice ?? entry.damageFormula
          };
        })
      : (typeof actionData.damage === 'string' && actionData.damage.trim()
        ? [{ formula: actionData.damage.trim(), type: actionData.damageType || 'untyped' }]
        : actionData.damage);

    return {
      ...actionData,
      save: {
        ...(explicitSave && typeof explicitSave === 'object' ? explicitSave : {}),
        ability,
        dc
      },
      damage: normalizedDamage
    };
  }

  static hasStructuredSavingThrow(actionData = {}) {
    return Boolean(ActionEconomyProcessor.normalizeSavingThrowAbility(actionData));
  }

  static async executeSavingThrowAbility(attacker, targets, actionData, combatState = {}) {
    const resolvedTargets = Array.isArray(targets) ? targets.filter(Boolean) : [];
    if (resolvedTargets.length === 0) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: actionData?.name || 'Ability',
        error: 'Saving throw ability requires at least one target'
      };
    }

    const abilityAction = ActionEconomyProcessor.normalizeSavingThrowAbility(actionData);
    if (!abilityAction) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: actionData?.name || 'Ability',
        error: 'Saving throw ability requires structured save ability and DC'
      };
    }

    const results = resolvedTargets.map((target) => {
      const saveResult = CombatManager.executeSavingThrow(
        target,
        abilityAction,
        actionData?.options || combatState?.modifiers || {}
      );

      return {
        ...saveResult,
        target: saveResult?.target || target?.name,
        targetId: saveResult?.targetId || target?.id
      };
    });
    const successfulResults = results.filter((entry) => entry?.success !== false);

    return {
      success: successfulResults.length > 0,
      actionType: ACTION_TYPES.ACTION,
      actionName: abilityAction.name || 'Ability',
      effect: `${abilityAction.name || 'Ability'} resolved against ${successfulResults.length} target(s)`,
      results,
      effects: results.map((entry) => ({
        type: 'savingThrow',
        target: entry?.target,
        targetId: entry?.targetId,
        savingThrow: entry?.savingThrow || null,
        damage: entry?.damage || null
      })),
      totalDamage: results.reduce((sum, entry) => sum + (Number(entry?.damage?.total) || 0), 0),
      actionData: abilityAction
    };
  }

  static async executeSpellCast(attacker, targets, actionData, combatState = {}) {
    const enrichedActionData = enrichSpellActionData(actionData || {});
    const spellName = String(enrichedActionData?.name || enrichedActionData?.spellName || 'Unknown Spell');
    const spellLevel = resolveSpellLevel(enrichedActionData?.spellLevel, enrichedActionData?.level, 1);
    const normalizedSpells = resolveCombatantSpellData(attacker, enrichedActionData) || attacker?.spells || {};

    attacker.spells = normalizedSpells;

    const spellValidation = await SpellHandler.validate(normalizedSpells, attacker?.level ?? 1);
    if (!spellValidation.valid) {
      return this.createFailureResult('Spell validation failed', spellValidation.errors);
    }

    attacker.spells = spellValidation.normalizedSpells || normalizedSpells;

    const spellMeta = SpellHandler.resolve(enrichedActionData, spellLevel);
    const resolution = classifySpellResolution(enrichedActionData, spellMeta);
    if (!resolution.supported) {
      const resolutionRequest = buildGameMasterSpellResolutionRequest(enrichedActionData, resolution);
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: spellName,
        error: resolution.reason,
        requiresGameMasterResolution: true,
        resolutionRequest,
        spellLevel,
        slotUsed: null,
        actionData: enrichedActionData
      };
    }

    const castingResult = SpellHandler.castSpell(
      attacker.spells,
      spellName,
      spellLevel,
      spellLevel > 0
    );

    attacker.spells = castingResult.updatedSpells || attacker.spells;

    if (!castingResult.success) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: spellName,
        error: castingResult.message,
        spellLevel,
        slotUsed: castingResult.slotUsed,
        result: castingResult,
        actionData
      };
    }

    const spellAction = {
      ...enrichedActionData,
      name: spellName,
      type: 'spell',
      level: spellLevel,
      spellLevel,
      attackBonus: resolution.mode === 'attack_roll'
        ? (enrichedActionData?.attackBonus ?? enrichedActionData?.bonus ?? enrichedActionData?.toHit ?? attacker?.spells?.attackBonus)
        : enrichedActionData?.attackBonus,
      toHit: resolution.mode === 'attack_roll'
        ? (enrichedActionData?.toHit ?? enrichedActionData?.attackBonus ?? enrichedActionData?.bonus ?? attacker?.spells?.attackBonus)
        : enrichedActionData?.toHit,
      saveDC: resolution.mode === 'saving_throw'
        ? (enrichedActionData?.saveDC ?? attacker?.spells?.saveDC)
        : enrichedActionData?.saveDC,
      rangeFeet: enrichedActionData?.rangeFeet ?? spellMeta?.range
    };
    if (spellAction.attack && typeof spellAction.attack === 'object' && spellAction.attack.toHit == null && spellAction.attackBonus != null) {
      spellAction.attack = {
        ...spellAction.attack,
        toHit: spellAction.attackBonus
      };
    }
    const spellOptions = {
      ...(combatState || {}),
      ...(enrichedActionData?.options || {}),
      spellLevel,
      meta: spellMeta || undefined
    };
    const targetPoint = enrichedActionData?.targetPoint || enrichedActionData?.point || enrichedActionData?.target || null;
    const spellResult = await CombatManager.executeSpell(
      attacker,
      spellAction,
      targetPoint,
      Array.isArray(targets) ? targets : [],
      combatState || {},
      spellOptions
    );

    return {
      success: !!spellResult?.success,
      actionType: ACTION_TYPES.ACTION,
      actionName: spellName,
      spell: spellName,
      effect: spellResult?.effect || castingResult.message,
      spellLevel,
      slotUsed: castingResult.slotUsed,
      updatedSpells: attacker.spells,
      // date_of_change: 2026-06-07
      // WHAT: Surface the spent spell-slot pool as a combat-stage update.
      // WHY: SpellHandler returns the updated canonical spell data, but callers usually
      // pass a projected combatant copy; the BattleRoom token snapshot must receive an
      // explicit update or the character sheet/right panel will continue showing stale slots.
      combatStageUpdates: {
        attacker: {
          spells: attacker.spells
        }
      },
      affectedTargets: spellResult?.affectedTargets || [],
      results: spellResult?.results || [],
      effects: spellResult?.effects || [],
      totalDamage: spellResult?.totalDamage || 0,
      totalHealing: spellResult?.totalHealing || 0,
      result: spellResult,
      actionData: spellAction
    };
  }

  /**
   * Execute main actions (Attack, Spell, Dash, etc.) with enhanced non-attack action support.
   * @date_of_change 2025-09-19
   */
  static async executeMainAction(actionName, attacker, targets, actionData, combatState) {
    if (actionData?.interactionIntent) {
      return this.executeDeclaredInteraction(actionName, actionData, ACTION_TYPES.ACTION);
    }

    // Enhanced spell detection and routing
    const spellArraysContain = (arr, name) => Array.isArray(arr) && arr.some(entry => {
      if (typeof entry === 'string') return entry === name;
      if (entry && typeof entry === 'object') return (entry.name === name) || (entry.id === name);
      return false;
    });

    const isSpellAction = actionData.type === 'spell' ||
                         actionData.spellLevel !== undefined ||
                         actionData.components ||
                         actionData.castingTime ||
                         actionData.school ||
                         (attacker.spells && (
                           spellArraysContain(attacker.spells.known, actionName) ||
                           spellArraysContain(attacker.spells.prepared, actionName) ||
                           spellArraysContain(attacker.spells.atWill, actionName)
                         ));

    if (isSpellAction) {
      return await this.executeSpellCast(attacker, targets, actionData, combatState);
    }

    if (
      String(actionData?.featureKey || '').trim().toLowerCase() === 'lay_on_hands'
      || (/^lay on hands$/i.test(String(actionName || '').trim()) && actionData?.type === 'feature')
    ) {
      return this.executeLayOnHands(attacker, targets, actionData);
    }

    switch (actionName) {
      case 'Attack':
        return await this.executeAttack(attacker, targets, actionData, combatState);

      case 'Cast a Spell':
        return await this.executeSpellCast(attacker, targets, actionData, combatState);

      case 'Dash':
        return this.executeDash(attacker, actionData, combatState);

      case 'Disengage':
        return this.executeDisengage(attacker, actionData, combatState);

      case 'Dodge':
        return this.executeDodge(attacker, actionData, combatState);

      case 'Help':
        return this.executeHelp(attacker, targets, actionData, combatState);

      case 'Hide':
        return await this.executeHide(attacker, actionData, combatState);

      case 'Ready':
        return this.executeReady(attacker, actionData, combatState);

      case 'Search':
        return await this.executeSearch(attacker, actionData, combatState);

      case 'Use an Object':
        return this.executeUseObject(attacker, actionData, combatState);

      default:
        if (isUseObjectAction(actionData)) {
          return this.executeUseObject(attacker, actionData, combatState);
        }

        if (ActionEconomyProcessor.hasStructuredSavingThrow(actionData)) {
          return await this.executeSavingThrowAbility(attacker, targets, actionData, combatState);
        }

        // Structured checks are resolved without attack targeting or attack-roll classification.
        if (ActionEconomyProcessor.hasStructuredActionCheck(actionData)) {
          return await this.executeActionCheck(attacker, actionData, combatState);
        }

        // Handle specific weapon attacks and multiattack components
        if (actionData?.damage || actionData?.attackBonus || actionData?.toHit ||
            actionData?.attack || actionData?.type === 'weapon' || actionData?.type === 'attack') {
          return await this.executeAttack(attacker, targets, actionData, combatState);
        }

        // If it's a spell action, route to spell execution
        if (actionData?.type === 'spell' || actionData?.castingTime || actionData?.components) {
          return await this.executeSpellCast(attacker, targets, actionData, combatState);
        }

        throw new Error(`${actionName} has no structured VCS mechanics. Record exact intent for GMA resolution instead.`);
    }
  }

  /**
   * Execute Dash action
   */
  static executeDash(attacker, actionData, combatState) {
    const standardAction = getStandardActionRule(actionData?.standardActionKey);
    const additionalMovement = ActionEconomyProcessor.parseSpeedFeet(attacker?.speed);

    try {
      // Synchronize turn-based movement allocation with movement engine
      movementTracker.useDash(attacker.id, false);
    } catch (e) {
      console.warn('[ActionEconomyProcessor] Failed to sync Dash with movementTracker:', e?.message || e);
    }

    return {
      success: true,
      actionType: ACTION_TYPES.ACTION,
      actionName: 'Dash',
      effect: `Gained ${additionalMovement} feet of additional movement`,
      standardAction,
      additionalMovement,
      dashUsed: true
    };
  }

  /**
   * Execute Disengage action  
   */
  static executeDisengage(attacker, actionData, combatState) {
    const standardAction = getStandardActionRule(actionData?.standardActionKey);
    return {
      success: true,
      actionType: ACTION_TYPES.ACTION,
      actionName: 'Disengage',
      effect: 'Movement does not provoke opportunity attacks until end of turn',
      standardAction,
      combatStageUpdates: {
        attacker: {
          combatFlags: {
            disengagedThisTurn: true
          }
        }
      },
      disengaged: true
    };
  }

  /**
   * Execute Dodge action
   */
  static executeDodge(attacker, actionData, combatState) {
    const standardAction = getStandardActionRule(actionData?.standardActionKey);
    return {
      success: true,
      actionType: ACTION_TYPES.ACTION,
      actionName: 'Dodge',
      effect: 'Attackers have disadvantage, you have advantage on Dex saves until start of next turn',
      standardAction,
      combatStageUpdates: {
        attacker: {
          combatFlags: {
            dodging: true
          }
        }
      },
      dodging: true
    };
  }

  /**
   * Execute Help action
   */
  static executeHelp(attacker, targets, actionData, combatState) {
    const standardAction = getStandardActionRule(actionData?.standardActionKey);
    if (targets.length === 0) {
      throw new Error('Help action requires a target');
    }

    const primaryTarget = targets[0] || null;

    return {
      success: true,
      actionType: ACTION_TYPES.ACTION,
      actionName: 'Help',
      effect: 'Target has advantage on their next ability check or attack',
      standardAction,
      combatStageUpdates: {
        attacker: {
          combatFlags: {
            helpTarget: primaryTarget?.id || null
          }
        }
      },
      targets,
      helping: true
    };
  }

  /**
   * Resolve the explicit map circumstance required before a 2024 Hide check.
   * The current map schema cannot deterministically classify obscurity or
   * three-quarters cover, so the controller/GM must declare one finite legal
   * circumstance rather than the runtime inferring it from prose.
   * rule_reference: D&D Free Rules (2024), Hide [Action]; date_of_change: 2026-09-06
   */
  static async resolveHideContext(attacker, actionData, combatState) {
    let hideContext = actionData?.hideContext;
    if (hideContext == null) {
      const choiceRequester = typeof combatState?.requestChoice === 'function'
        ? combatState.requestChoice
        : requestChoice;
      const choice = await choiceRequester({
        title: 'Declare Hide Circumstance',
        prompt: `${attacker?.name || attacker?.id || 'This creature'} can attempt to Hide only with qualifying concealment or cover and while outside every enemy's line of sight.`,
        options: Object.keys(HIDE_CONTEXT_CHOICES)
      });
      if (!choice) return null;
      hideContext = {
        qualifyingCondition: HIDE_CONTEXT_CHOICES[String(choice)],
        enemyLineOfSight: false,
        source: 'controller-declaration'
      };
    }

    const actionIdentifier = actionData?.id || actionData?.standardActionKey || actionData?.name || 'hide';
    if (!hideContext || typeof hideContext !== 'object' || Array.isArray(hideContext)) {
      throw new Error(
        `Hide action ${actionIdentifier} for ${attacker?.id || 'unknown-attacker'}: actionData.hideContext must be an object. See Character_Monster_Data_Architecture.md.`
      );
    }
    if (!HIDE_QUALIFYING_CONDITIONS.has(hideContext.qualifyingCondition)) {
      throw new Error(
        `Hide action ${actionIdentifier} for ${attacker?.id || 'unknown-attacker'}: actionData.hideContext.qualifyingCondition must be one of heavily-obscured, three-quarters-cover, or total-cover. See Character_Monster_Data_Architecture.md.`
      );
    }
    if (hideContext.enemyLineOfSight !== false) {
      throw new Error(
        `Hide action ${actionIdentifier} for ${attacker?.id || 'unknown-attacker'}: actionData.hideContext.enemyLineOfSight must be exactly false because no enemy may have line of sight. See Character_Monster_Data_Architecture.md.`
      );
    }

    return {
      qualifyingCondition: hideContext.qualifyingCondition,
      enemyLineOfSight: false,
      source: hideContext.source || 'structured-action-data'
    };
  }

  /**
   * Execute the 2024 Hide action: prove a legal circumstance, then make a
   * fixed-DC Dexterity (Stealth) check. The successful total becomes the DC
   * for a later Wisdom (Perception) check to find the hidden creature.
   * rule_reference: D&D Free Rules (2024), Hide [Action]; date_of_change: 2026-09-06
   */
  static async executeHide(attacker, actionData, combatState) {
    const standardAction = getStandardActionRule(actionData?.standardActionKey);
    const resolvedActionType = actionData?.standardActionKey === 'bonus-action-hide'
      ? ACTION_TYPES.BONUS_ACTION
      : ACTION_TYPES.ACTION;
    const hideContext = await ActionEconomyProcessor.resolveHideContext(attacker, actionData, combatState);
    if (!hideContext) {
      return {
        success: false,
        cancelled: true,
        code: 'ACTION_CANCELLED',
        actionType: resolvedActionType,
        actionName: 'Hide',
        effect: 'Hide circumstance declaration was cancelled; no action was spent.'
      };
    }
    const modifierResolution = await ActionEconomyProcessor.resolveCheckModifiers(
      attacker,
      combatState?.modifiers || {},
      'Stealth check',
      combatState
    );
    if (!modifierResolution.success) {
      return {
        ...modifierResolution,
        actionType: resolvedActionType,
        actionName: 'Hide'
      };
    }
    // Stealth check through CombatManager
    const stealthCheck = CombatManager.rollSkillCheck(
      attacker,
      'stealth', 
      modifierResolution.modifiers
    );
    if (stealthCheck?.error) {
      return {
        success: false,
        actionType: resolvedActionType,
        actionName: 'Hide',
        effect: 'The Stealth check could not be resolved.',
        error: stealthCheck.error,
        checkError: stealthCheck.error,
        stealthCheck
      };
    }
    const isHidden = Number(stealthCheck.total) >= HIDE_DC;
    const hiddenFrom = isHidden
      ? ActionEconomyProcessor.resolveHideObservers(attacker, combatState)
      : [];
    const checkRoll = ActionEconomyProcessor.normalizeCheckRoll(stealthCheck);
    const hiddenStateUpdate = isHidden
      ? {
          conditions: ['Hidden'],
          conditionsToRemove: [],
          combatFlags: {
            hiddenFrom,
            hiddenStealthCheck: checkRoll
          }
        }
      : {
          conditions: [],
          conditionsToRemove: ['Hidden'],
          combatFlags: {
            hiddenFrom: [],
            hiddenStealthCheck: null
          }
        };

    return {
      success: true,
      actionType: resolvedActionType,
      actionName: 'Hide',
      effect: isHidden
        ? `Hidden with the Invisible condition; discovery DC ${stealthCheck.total}.`
        : `The Dexterity (Stealth) total ${stealthCheck.total} did not meet Hide DC ${HIDE_DC}.`,
      standardAction,
      combatStageUpdates: {
        attacker: hiddenStateUpdate
      },
      stealthCheck: checkRoll || stealthCheck,
      checkRoll,
      rolls: ActionEconomyProcessor.buildCheckAuditRoll(checkRoll || stealthCheck),
      hiddenFrom,
      hiddenCheck: checkRoll,
      hideContext,
      hideDC: HIDE_DC,
      discoveryDC: isHidden ? stealthCheck.total : null,
      grantedCondition: isHidden ? 'Invisible' : null,
      hiding: isHidden
    };
  }

  /**
   * Execute Ready action
   */
  static executeReady(attacker, actionData, combatState) {
    const rawTrigger = actionData?.trigger;
    const response = actionData?.response || actionData?.readiedAction;
    const standardAction = getStandardActionRule(actionData?.standardActionKey);

    const trigger = typeof rawTrigger === 'string'
      ? { event: 'manual', description: rawTrigger.trim(), actorId: null, targetId: null }
      : {
          event: String(rawTrigger?.event || rawTrigger?.type || '').trim().toLowerCase().replace(/[ -]+/g, '_'),
          description: String(rawTrigger?.description || rawTrigger?.condition || '').trim(),
          actorId: rawTrigger?.actorId || null,
          targetId: rawTrigger?.targetId || null
        };
    const supportedEvents = ['movement', 'attack', 'spell', 'action', 'object_interaction', 'manual'];
    if (!supportedEvents.includes(trigger.event) || !trigger.description || !response) {
      throw new Error('Ready requires a structured observable trigger and an executable response action');
    }
    const responseType = String(response?.type || response?.actionType || '').trim().toLowerCase();
    if (!['attack', 'spell', 'check', 'save'].includes(responseType) || !String(response?.name || '').trim()) {
      throw new Error('Ready response must be a named executable attack, spell, check, or save');
    }

    const declaration = {
      id: `ready-${attacker.id}-${Number.isFinite(combatState?.round) ? combatState.round : 0}-${Number.isFinite(combatState?.turn) ? combatState.turn : 0}`,
      actorId: attacker.id,
      status: 'readied',
      trigger,
      response,
      declaredRound: Number.isFinite(combatState?.round) ? combatState.round : 0,
      declaredTurn: Number.isFinite(combatState?.turn) ? combatState.turn : 0
    };

    // Mirror the service-side canonical Ready declaration. Persistence uses the
    // token combatFlags stage update below; VCS service integrations use the
    // same trigger/response/status field names.
    combatState.readiedActions = combatState.readiedActions || {};
    combatState.readiedActions[attacker.id] = declaration;

    return {
      success: true,
      actionType: ACTION_TYPES.ACTION,
      actionName: 'Ready',
      effect: `Readied "${response.name}" for: ${trigger.description}`,
      standardAction,
      combatStageUpdates: {
        attacker: {
          combatFlags: {
            readiedAction: declaration
          }
        }
      },
      trigger,
      response,
      readiedAction: response,
      actionSpent: true,
      reactionReserved: true
    };
  }

  /**
   * Execute Search action
   */
  static async executeSearch(attacker, actionData, combatState) {
    const standardAction = getStandardActionRule(actionData?.standardActionKey);
    const requestedSkill = String(actionData?.searchSkill || '').trim().toLowerCase();
    const requestedSense = String(actionData?.searchSense || actionData?.check?.sense || '').trim().toLowerCase();
    const declarationError = (path, expected) => ({
      success: false,
      actionType: ACTION_TYPES.ACTION,
      actionName: 'Search',
      code: 'SEARCH_DECLARATION_INVALID',
      effect: 'Search was not resolved; no action was spent.',
      error: `${attacker?.id || 'unknown combatant'}/search: ${path} must be ${expected}. See Character_Monster_Data_Architecture.md.`
    });

    // Search context is a player/GM declaration. Never infer a skill or sense
    // from the action name or a creature trait at runtime.
    // rule_reference: SRD 5.1 Search action and passive checks; date_of_change: 2026-09-06.
    if (!['perception', 'investigation'].includes(requestedSkill)) {
      return declarationError('actionData.searchSkill', '"perception" or "investigation"');
    }
    if (requestedSkill === 'perception' && !['general', 'sight', 'hearing', 'smell'].includes(requestedSense)) {
      return declarationError('actionData.searchSense', '"general", "sight", "hearing", or "smell" for a Perception check');
    }
    if (requestedSkill === 'investigation' && requestedSense) {
      return declarationError('actionData.searchSense', 'omitted for an Investigation check');
    }

    const checkContext = {
      ability: requestedSkill === 'perception' ? 'wisdom' : 'intelligence',
      skill: requestedSkill,
      ...(requestedSkill === 'perception' && requestedSense !== 'general' ? { sense: requestedSense } : {})
    };
    const creatureAdvantageSources = creatureCheckAdvantageSources(attacker, checkContext);
    const modifierResolution = await ActionEconomyProcessor.resolveCheckModifiers(
      attacker,
      {
        ...creatureCheckModifiers(attacker, checkContext),
        ...(combatState?.modifiers || {})
      },
      `${requestedSkill} check`,
      combatState
    );
    if (!modifierResolution.success) {
      return {
        ...modifierResolution,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Search',
        searchSkill: requestedSkill
      };
    }
    // Perception/Investigation check through CombatManager
    const searchCheck = CombatManager.rollSkillCheck(
      attacker,
      requestedSkill,
      modifierResolution.modifiers
    );
    if (searchCheck?.error) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Search',
        effect: `The ${requestedSkill} check could not be resolved.`,
        error: searchCheck.error,
        checkError: searchCheck.error,
        searchCheck,
        searchSkill: requestedSkill
      };
    }
    const checkRoll = ActionEconomyProcessor.normalizeCheckRoll(searchCheck);

    return {
      success: true,
      actionType: ACTION_TYPES.ACTION,
      actionName: 'Search',
      effect: requestedSkill === 'perception'
        ? `Made a Wisdom (Perception) check using ${requestedSense === 'general' ? 'general awareness' : requestedSense}${creatureAdvantageSources.length ? ` with advantage from ${creatureAdvantageSources.join(', ')}` : ''}`
        : 'Made an Intelligence (Investigation) check to search the area',
      standardAction,
      perceptionCheck: requestedSkill === 'perception' ? searchCheck : null,
      investigationCheck: requestedSkill === 'investigation' ? searchCheck : null,
      searchCheck: checkRoll || searchCheck,
      checkRoll,
      rolls: ActionEconomyProcessor.buildCheckAuditRoll(checkRoll || searchCheck),
      searchSkill: requestedSkill,
      searchSense: requestedSkill === 'perception' ? requestedSense : null,
      creatureAdvantageSources,
      searching: true
    };
  }

  /**
   * Execute Use an Object action
   */
  static executeUseObject(attacker, actionData, combatState) {
    const standardAction = getStandardActionRule(actionData?.standardActionKey);
    const objectName = String(actionData?.objectName ?? '').trim();
    const objectEffect = String(actionData?.objectEffect ?? '').trim();
    const objectId = actionData?.objectId || actionData?.objectTokenId || actionData?.targetId || null;
    
    if (!objectName) {
      throw new Error('Use Object action requires object name');
    }

    const declared = this.executeDeclaredInteraction(
      'Use an Object',
      {
        ...actionData,
        objectName,
        objectEffect,
        objectId,
        interactionIntent: actionData?.interactionIntent
      },
      ACTION_TYPES.ACTION
    );

    return {
      ...declared,
      standardAction,
      objectName,
      objectEffect,
      objectId,
      objectTokenId: actionData?.objectTokenId || null
    };
  }

  static resolvePassivePerception(combatant) {
    return CombatManager.resolvePassivePerception(combatant);
  }

  static resolveHideObservers(attacker, combatState) {
    const enemies = Array.isArray(combatState?.tokens)
      ? combatState.tokens.filter((token) => (
          token?.id
          && areCombatEnemies(attacker, token)
          && !isCombatantIncapacitated(token)
        ))
      : [];

    return enemies.map((token) => token.id);
  }

  /**
   * Execute bonus action (similar to main action but different economy)
   */
  static async executeBonusAction(actionName, attacker, targets, actionData, combatState) {
    if (actionData?.interactionIntent) {
      return this.executeDeclaredInteraction(actionName, actionData, ACTION_TYPES.BONUS_ACTION);
    }
    if (
      String(actionData?.featureKey || '').trim().toLowerCase() === 'rage'
      || (/^rage$/i.test(String(actionName || '').trim()) && actionData?.type === 'feature')
    ) {
      return this.executeRage(attacker, actionData, combatState);
    }
    if (/^steady aim$/i.test(String(actionName || '').trim())) {
      return this.executeSteadyAim(attacker, actionData, combatState);
    }

    // Handle explicit bonus action Dash
    const isBonusDash = actionName === 'Bonus Action Dash'
      || actionData?.dashAction
      || actionData?.standardActionKey === 'bonus-action-dash';
    if (isBonusDash) {
      let additionalMovement = 30;
      try {
        additionalMovement = ActionEconomyProcessor.parseSpeedFeet(attacker?.speed);
      } catch (_) {}

      try {
        movementTracker.useDash(attacker.id, true);
      } catch (e) {
        console.warn('[ActionEconomyProcessor] Failed to sync Bonus Action Dash with movementTracker:', e?.message || e);
      }

      return {
        success: true,
        actionType: ACTION_TYPES.BONUS_ACTION,
        actionName: actionData?.name || 'Dash',
        effect: `Gained ${additionalMovement} feet of additional movement`,
        standardAction: getStandardActionRule(actionData?.standardActionKey),
        additionalMovement,
        dashUsed: true
      };
    }

    // Only mechanically structured bonus actions may enter the corresponding
    // attack/spell/check/save resolver. Description-only feature data is not a
    // rules implementation and must collect explicit intent instead.
    // rule_reference: AGENTS.md - never infer mechanics from prose.
    // date_of_change: 2026-07-18
    const delegatesToKnownMainAction = [
      'Attack', 'Cast a Spell', 'Dash', 'Disengage', 'Dodge',
      'Help', 'Hide', 'Ready', 'Search', 'Use an Object'
    ].includes(actionName);
    if (
      delegatesToKnownMainAction
      ||
      ActionEconomyProcessor.hasStructuredSavingThrow(actionData)
      || ActionEconomyProcessor.hasStructuredActionCheck(actionData)
      || actionData?.attack
      || actionData?.type === 'attack'
      || actionData?.type === 'weapon'
      || actionData?.type === 'spell'
      || actionData?.castingTime
      || actionData?.components
    ) {
      const result = await this.executeMainAction(actionName, attacker, targets, actionData, combatState);
      result.actionType = ACTION_TYPES.BONUS_ACTION;
      return result;
    }

    return {
      success: false,
      actionType: ACTION_TYPES.BONUS_ACTION,
      actionName,
      code: 'INTERACTION_INTENT_REQUIRED',
      error: `${actionName} needs a target and exact intent before GMA can resolve it.`,
      requiresInteractionIntent: true
    };
  }

  static executeRage(attacker, actionData, combatState) {
    const mechanics = actionData?.mechanics;
    const resourceKey = mechanics?.resourceKey;
    if (
      !mechanics
      || mechanics.targeting !== 'self'
      || typeof resourceKey !== 'string'
      || !resourceKey.trim()
      || !Number.isInteger(mechanics.durationRounds)
      || mechanics.durationRounds < 1
      || !Number.isFinite(mechanics.meleeStrengthDamageBonus)
      || !Array.isArray(mechanics.damageResistances)
      || mechanics.damageResistances.length === 0
      || mechanics.damageResistances.some((entry) => typeof entry !== 'string' || !entry.trim())
    ) {
      return {
        success: false,
        actionType: ACTION_TYPES.BONUS_ACTION,
        actionName: actionData?.name,
        error: 'Rage requires complete structured mechanics including resourceKey, duration, damage bonus, and resistances'
      };
    }
    const existingResource = attacker?.combatResources?.[resourceKey] || {};
    const actionUses = actionData?.uses && typeof actionData.uses === 'object'
      ? actionData.uses
      : {};
    const maxUses = existingResource.max ?? actionUses.max;
    const currentUses = existingResource.current ?? actionUses.current;
    const reset = existingResource.reset ?? actionUses.reset;

    if (!Number.isInteger(maxUses) || maxUses <= 0 || !Number.isInteger(currentUses) || currentUses < 0 || currentUses > maxUses || typeof reset !== 'string' || !reset) {
      return {
        success: false,
        actionType: ACTION_TYPES.BONUS_ACTION,
        actionName: 'Rage',
        error: 'Rage requires an explicit uses resource'
      };
    }
    if (currentUses <= 0) {
      return {
        success: false,
        actionType: ACTION_TYPES.BONUS_ACTION,
        actionName: 'Rage',
        error: 'No Rage uses remaining'
      };
    }
    if (attacker?.statusEffects?.rage === true || attacker?.combatFlags?.rageActive === true) {
      return {
        success: false,
        actionType: ACTION_TYPES.BONUS_ACTION,
        actionName: 'Rage',
        error: 'Rage is already active'
      };
    }

    const remainingUses = currentUses - 1;
    const roundNow = Number(combatState?.round ?? combatState?.currentRound ?? CombatLogManager?.currentRound) || 1;
    const turnNow = Number(combatState?.turn ?? combatState?.currentTurn ?? CombatLogManager?.currentTurn) || 0;
    const rageResource = {
      ...existingResource,
      max: maxUses,
      current: remainingUses,
      reset
    };

    return {
      success: true,
      actionType: ACTION_TYPES.BONUS_ACTION,
      actionName: 'Rage',
      effect: `Rage active — ${remainingUses}/${maxUses} uses remaining`,
      combatStageUpdates: {
        attacker: {
          combatFlags: {
            rageActive: true
          },
          statusEffects: {
            rage: true
          },
          statusEffectsMeta: {
            rage: {
              ...mechanics,
              sourceAction: 'Rage',
              sourceId: attacker?.id,
              startedRound: roundNow,
              startedTurn: turnNow,
              roundsRemaining: mechanics.durationRounds
            }
          },
          combatResources: {
            ...(attacker?.combatResources || {}),
            [resourceKey]: { ...rageResource, reset }
          }
        }
      },
      rage: {
        active: true,
        usesRemaining: remainingUses,
        maxUses,
        mechanics
      },
      actionData
    };
  }

  static executeLayOnHands(attacker, targets, actionData) {
    const targetList = Array.isArray(targets) ? targets : [];
    const target = targetList[0] || null;
    if (!target?.id || targetList.length !== 1) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Lay on Hands',
        error: 'Lay on Hands requires exactly one creature target'
      };
    }

    const mechanics = actionData?.mechanics;
    const resourceKey = mechanics?.resourceKey;
    const spendRules = mechanics?.resourceSpend;
    if (
      mechanics?.targeting !== 'creature'
      || typeof resourceKey !== 'string'
      || !resourceKey.trim()
      || spendRules?.type !== 'healing'
      || !Number.isInteger(spendRules.min)
      || !Number.isInteger(spendRules.max)
      || spendRules.min < 1
      || spendRules.max < spendRules.min
    ) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: actionData?.name,
        error: 'Lay on Hands requires complete creature-targeting resourceSpend mechanics'
      };
    }
    const existingResource = attacker?.combatResources?.[resourceKey] || {};
    const actionUses = actionData?.uses && typeof actionData.uses === 'object'
      ? actionData.uses
      : {};
    const maxUses = existingResource.max ?? actionUses.max;
    const currentUses = existingResource.current ?? actionUses.current;
    const reset = existingResource.reset ?? actionUses.reset;
    const requestedSpend = actionData?.resourceSpend;

    if (!Number.isInteger(maxUses) || maxUses <= 0 || !Number.isInteger(currentUses) || currentUses < 0 || currentUses > maxUses || typeof reset !== 'string' || !reset) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Lay on Hands',
        error: 'Lay on Hands requires an explicit healing-pool resource'
      };
    }
    if (currentUses <= 0) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Lay on Hands',
        error: 'No Lay on Hands points remaining'
      };
    }
    if (!Number.isFinite(requestedSpend) || requestedSpend < spendRules.min || requestedSpend > spendRules.max || requestedSpend > currentUses) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Lay on Hands',
        error: `Choose between ${spendRules.min} and ${Math.min(spendRules.max, currentUses)} Lay on Hands points`
      };
    }

    const creatureType = typeof target?.creatureType === 'string'
      ? target.creatureType.trim().toLowerCase()
      : '';
    if (!creatureType) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Lay on Hands',
        error: 'target.creatureType must be a canonical non-empty string'
      };
    }
    if (creatureType.includes('undead') || creatureType.includes('construct')) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Lay on Hands',
        error: 'Lay on Hands has no effect on undead or constructs'
      };
    }

    const hpBefore = target?.hp;
    const maxHp = target?.maxHp;
    if (!Number.isFinite(hpBefore) || !Number.isFinite(maxHp)) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Lay on Hands',
        error: 'Target is missing canonical hit point data'
      };
    }
    if (hpBefore >= maxHp) {
      return {
        success: false,
        actionType: ACTION_TYPES.ACTION,
        actionName: 'Lay on Hands',
        error: `${target.name || 'Target'} is already at full hit points`
      };
    }

    const updatedTarget = CombatManager.applyHealing(target, requestedSpend);
    const hpAfter = updatedTarget?.hp;
    const actualHealing = Math.max(0, hpAfter - hpBefore);
    const remainingUses = currentUses - requestedSpend;
    const healing = {
      total: actualHealing,
      spent: requestedSpend,
      formula: `${requestedSpend} Lay on Hands point${requestedSpend === 1 ? '' : 's'}`,
      type: 'class feature'
    };
    const targetResult = {
      target: target.name || 'Target',
      targetId: target.id,
      originalTarget: target,
      updatedTarget,
      targetHpBefore: hpBefore,
      targetHpAfter: hpAfter,
      healing
    };

    return {
      success: true,
      actionType: ACTION_TYPES.ACTION,
      actionName: 'Lay on Hands',
      effect: `Restored ${actualHealing} HP to ${target.name || 'the target'} — ${remainingUses}/${maxUses} points remaining`,
      targetHpBefore: hpBefore,
      targetHpRemaining: hpAfter,
      results: [targetResult],
      affectedTargets: [targetResult],
      effects: [{
        type: 'healing',
        target: target.name || 'Target',
        targetId: target.id,
        healing
      }],
      totalHealing: actualHealing,
      combatStageUpdates: {
        attacker: {
          combatResources: {
            ...(attacker?.combatResources || {}),
            [resourceKey]: {
              ...existingResource,
              max: maxUses,
              current: remainingUses,
              reset
            }
          }
        }
      },
      actionData
    };
  }

  static executeSecondWind(attacker, actionData) {
    const mechanics = actionData?.mechanics;
    const resourceKey = mechanics?.resourceKey;
    const healingDice = mechanics?.healingDice;
    const actionUses = actionData?.uses;
    const existingResource = attacker?.combatResources?.[resourceKey];
    const maxUses = existingResource?.max ?? actionUses?.max;
    const currentUses = existingResource?.current ?? actionUses?.current;
    const reset = existingResource?.reset ?? actionUses?.reset;

    if (mechanics?.targeting !== 'self' || typeof resourceKey !== 'string' || !resourceKey.trim()) {
      return { success: false, actionName: actionData?.name, error: 'Second Wind requires explicit self-targeting and resourceKey mechanics' };
    }
    if (typeof healingDice !== 'string' || !/^\d+d\d+(?:[+-]\d+)?$/.test(healingDice)) {
      return { success: false, actionName: actionData?.name, error: 'Second Wind requires an explicit healingDice formula' };
    }
    if (!Number.isInteger(maxUses) || maxUses < 1 || !Number.isInteger(currentUses) || currentUses < 0 || currentUses > maxUses || typeof reset !== 'string' || !reset) {
      return { success: false, actionName: actionData?.name, error: 'Second Wind requires a complete uses resource' };
    }
    if (currentUses === 0) {
      return { success: false, actionName: actionData?.name, error: 'No Second Wind uses remaining' };
    }

    const hpBefore = attacker.hp;
    const maxHp = attacker.maxHp;
    if (!Number.isFinite(hpBefore) || !Number.isFinite(maxHp)) {
      return { success: false, actionName: actionData?.name, error: 'attacker.hp and attacker.maxHp must be canonical numbers' };
    }
    if (hpBefore >= maxHp) {
      return { success: false, actionName: actionData?.name, error: `${attacker.name} is already at full hit points` };
    }

    const healingRoll = CombatManager.rollDice(healingDice, true, null, {
      manualRollQueue: actionData?.__manualRollQueue,
      label: `${actionData.name} healing`
    });
    if (healingRoll?.error || !Number.isFinite(healingRoll?.total)) {
      return { success: false, actionName: actionData?.name, error: 'Second Wind healing roll could not be resolved' };
    }
    const hpAfter = Math.min(maxHp, hpBefore + Math.max(0, healingRoll.total));
    const totalHealing = hpAfter - hpBefore;
    const remainingUses = currentUses - 1;

    return {
      success: true,
      actionName: actionData.name,
      effect: `Restored ${totalHealing} HP — ${remainingUses}/${maxUses} uses remaining`,
      totalHealing,
      rolls: [{ purpose: 'healing', formula: healingDice, dice: healingRoll.rolls || [], total: healingRoll.total }],
      combatStageUpdates: {
        attacker: {
          hp: hpAfter,
          combatResources: {
            ...(attacker.combatResources || {}),
            [resourceKey]: { ...(existingResource || {}), max: maxUses, current: remainingUses, reset }
          }
        }
      },
      actionData
    };
  }

  static executeSteadyAim(attacker, actionData, combatState) {
    const speed = ActionEconomyProcessor.parseSpeedFeet(attacker?.speed);
    const canonicalFlags = attacker?.combatFlags && typeof attacker.combatFlags === 'object'
      ? attacker.combatFlags
      : null;
    const hasCanonicalMovement = canonicalFlags
      && Object.prototype.hasOwnProperty.call(canonicalFlags, 'movementUsed')
      && Number.isFinite(Number(canonicalFlags.movementUsed));
    if (hasCanonicalMovement) {
      // Keep the transient movement validator on the same turn snapshot before
      // applying Steady Aim's lock; otherwise an old tracker entry can reject
      // a legal action (or permit an illegal one) during a refresh race.
      // date_of_change: 2026-07-18
      movementTracker.synchronizeTurnSnapshot(attacker.id, {
        ...attacker,
        // Preserve the canonical grid position when pixel coordinates are not
        // present; resetting to (0,0) would create a second movement baseline.
        // rule_reference: AGENTS.md — never create split-brain movement state.
        // date_of_change: 2026-07-18
        x: Number.isFinite(Number(attacker?.x)) ? attacker.x : (Number.isFinite(Number(attacker?.gridX)) ? attacker.gridX : 0),
        y: Number.isFinite(Number(attacker?.y)) ? attacker.y : (Number.isFinite(Number(attacker?.gridY)) ? attacker.gridY : 0),
        combatFlags: canonicalFlags,
        conditions: Array.isArray(attacker?.conditions) ? attacker.conditions : []
      });
    }
    const economySummary = ActionEconomyManager.getActionSummary(attacker.id, speed);
    const movementSummary = movementTracker.getMovementSummary(attacker.id);
    // Prefer the persisted token value when present. The transient movement
    // tracker can briefly describe the previous turn while a drag/initiative
    // snapshot is being reconciled; it must never veto a legal action based on
    // that stale copy. date_of_change: 2026-07-18
    const movementUsed = hasCanonicalMovement
      ? Math.max(0, Number(canonicalFlags.movementUsed))
      : Math.max(
        Number(economySummary?.movement?.used || 0),
        Number(movementSummary?.usedMovement || 0)
      );
    if (movementUsed > 0) {
      return {
        success: false,
        actionType: ACTION_TYPES.BONUS_ACTION,
        actionName: 'Steady Aim',
        error: 'Steady Aim requires that you have not moved during this turn'
      };
    }

    const reason = 'Steady Aim reduces Speed to 0 for the rest of this turn';
    const economyLock = ActionEconomyManager.lockMovement(attacker.id, reason);
    if (!economyLock.success) {
      return { success: false, actionType: ACTION_TYPES.BONUS_ACTION, actionName: 'Steady Aim', error: economyLock.reason };
    }
    const movementLock = movementTracker.lockMovement(attacker.id, reason);
    if (!movementLock.success && movementLock.error !== 'No active turn for this token') {
      return { success: false, actionType: ACTION_TYPES.BONUS_ACTION, actionName: 'Steady Aim', error: movementLock.error };
    }

    return {
      success: true,
      actionType: ACTION_TYPES.BONUS_ACTION,
      actionName: 'Steady Aim',
      effect: 'Advantage on the next attack roll this turn; Speed is 0 for the rest of the turn',
      combatStageUpdates: {
        attacker: {
          combatFlags: {
            steadyAimActive: true,
            movementLockedThisTurn: true,
            movementLockReason: reason,
            bonusActionUsed: true
          }
        }
      },
      actionData
    };
  }

  /**
   * Execute movement with speed tracking
   */
  static async executeMovement(attacker, actionData, combatState) {
    const { distance, terrain, movementCost } = actionData;
    if (!Number.isFinite(distance) || distance < 0) {
      throw new Error('actionData.distance must be a non-negative number of feet.');
    }
    if (!['normal', 'difficult'].includes(terrain)) {
      throw new Error('actionData.terrain must be normal or difficult.');
    }
    if (!Number.isFinite(movementCost) || movementCost < distance) {
      throw new Error('actionData.movementCost must be an explicit movement cost at least as large as distance.');
    }

    return {
      success: true,
      actionType: ACTION_TYPES.MOVEMENT,
      actionName: 'Move',
      effect: `Moved ${distance} feet${terrain === 'difficult' ? ' (difficult terrain)' : ''}`,
      distance,
      movementCost,
      terrain
    };
  }

  /**
   * Execute reaction
   */
  static async executeReaction(actionName, attacker, targets, actionData, combatState) {
    if (actionData?.interactionIntent) {
      return this.executeDeclaredInteraction(actionName, actionData, ACTION_TYPES.REACTION);
    }
    // Reactions are typically triggered by other events
    const result = await this.executeMainAction(actionName, attacker, targets, actionData, combatState);
    result.actionType = ACTION_TYPES.REACTION;
    return result;
  }

  /**
   * Execute free actions
   */
  static async executeFreeAction(actionName, attacker, actionData, combatState, actionType = ACTION_TYPES.FREE_ACTION) {
    const normalizedName = String(actionName || '').trim().toLowerCase();
    if (normalizedName === 'speak') {
      const speech = actionData?.speech;
      if (!speech?.text || !String(speech.text).trim()) {
        return {
          success: false,
          actionType,
          actionName,
          code: 'INTERACTION_INTENT_REQUIRED',
          error: 'Enter what the combatant says and who should hear it.',
          requiresInteractionIntent: true
        };
      }
      return {
        success: true,
        actionType,
        actionName,
        effect: `Speech recorded${speech.targetName ? ` for ${speech.targetName}` : ''}`,
        resolutionStatus: 'recorded',
        speech: { ...speech, text: String(speech.text).trim() },
        actionData
      };
    }

    if (actionData?.interactionIntent) {
      return this.executeDeclaredInteraction(actionName, actionData, actionType);
    }

    return {
      success: false,
      actionType,
      actionName,
      code: 'INTERACTION_INTENT_REQUIRED',
      error: `${actionName} needs exact intent before it can be recorded.`,
      requiresInteractionIntent: true
    };
  }

  /**
   * Record a player declaration without fabricating a mechanical outcome.
   * GMA receives the exact text and target and can resolve it against the
   * authoritative scene. The action economy is still consumed for action,
   * bonus-action, reaction, and free-object declarations.
   * date_of_change: 2026-07-18
   */
  static executeDeclaredInteraction(actionName, actionData, actionType) {
    const intent = actionData?.interactionIntent;
    if (!intent?.text || !String(intent.text).trim()) {
      return {
        success: false,
        actionType,
        actionName,
        code: 'INTERACTION_INTENT_REQUIRED',
        error: `${actionName} needs a target and exact intent before GMA can resolve it.`,
        requiresInteractionIntent: true
      };
    }
    return {
      success: true,
      actionType,
      actionName,
      effect: `Intent recorded for GMA resolution${intent.targetName ? `: ${intent.targetName}` : ''}`,
      resolutionStatus: 'declared',
      requiresGameMasterResolution: true,
      interactionIntent: { ...intent, text: String(intent.text).trim() },
      actionData
    };
  }

  /**
   * Log action result to CombatLogManager
   */
  static logAction(result, attacker, targets, gmOverride, actionRequest = null, combatState = null) {
    // @date_of_change 2025-09-17 - DEBUG: Trace logAction entry
    console.log('[ActionEconomyProcessor] logAction called with:', {
      resultSuccess: result.success,
      actionName: result.actionName,
      attackerName: attacker.name,
      targetCount: targets.length,
      hasActionRequest: !!actionRequest,
      hasCombatState: !!combatState
    });
    // @date_of_change 2025-09-10 - Fix CombatLogManager.logAction parameter mismatch
    // CombatLogManager expects (combatantId, action, targets, result) not a single object
    // @date_of_change 2025-09-15 - Normalize action.type for CombatLogManager
    // Map internal ACTION_TYPES to CombatLogManager types: 'attack'|'spell'|'move'|'bonus'|'reaction'|'free'
    const isSpell = !!(actionRequest?.actionData?.type === 'spell' || result?.spellLevel != null);
    const hasSavingThrowResults = Boolean(
      (Array.isArray(result?.results) && result.results.some((entry) => entry?.savingThrow))
      || (Array.isArray(result?.result?.results) && result.result.results.some((entry) => entry?.savingThrow))
      || (Array.isArray(result?.effects) && result.effects.some((entry) => entry?.type === 'savingThrow'))
    );
    const isAttack = !hasSavingThrowResults && !!(
      (result?.results && Array.isArray(result.results) && result.results.length > 0) ||
      actionRequest?.actionData?.attack ||
      actionRequest?.actionData?.type === 'attack' ||
      actionRequest?.actionData?.toHit ||
      (typeof actionRequest?.actionData?.attackBonus === 'number')
    );

    const typeFromActionType = (() => {
      switch (result?.actionType) {
        case ACTION_TYPES.ACTION: return 'action';
        case ACTION_TYPES.MOVEMENT: return 'move';
        case ACTION_TYPES.BONUS_ACTION: return 'bonus';
        case ACTION_TYPES.REACTION: return 'reaction';
        case ACTION_TYPES.FREE_ACTION: return 'free';
        case ACTION_TYPES.FREE_OBJECT: return 'free';
        default: return null;
      }
    })();

    const resolvedType = isSpell
      ? 'spell'
      : (hasSavingThrowResults ? (typeFromActionType || 'action') : (isAttack ? 'attack' : (typeFromActionType || 'action')));

    // @date_of_change 2025-10-12 - Limit combat log payload size while keeping GM-audit essentials (rule_reference: Engine transparency & deterministic logs)
    const pruneActionData = (data, depth = 0) => {
      if (!data || typeof data !== 'object') return data;
      if (depth > 2) {
        const shallow = {};
        for (const key of ['name', 'type', 'damage', 'range', 'attackType', 'saveType', 'saveDC', 'damageType', 'healing', 'components', 'castingTime', 'level', 'check', 'skillCheck', 'abilityCheck', 'dc', 'cunningStrike']) {
          if (data[key] !== undefined) shallow[key] = data[key];
        }
        return shallow;
      }
      const allowedKeys = ['name', 'type', 'actionType', 'attackType', 'range', 'damage', 'desc', 'description', 'saveType', 'saveDC', 'damageType', 'healing', 'components', 'castingTime', 'level', 'area', 'areaOfEffect', 'options', 'bonus', 'check', 'skillCheck', 'abilityCheck', 'dc', 'cunningStrike'];
      const clone = {};
      for (const key of allowedKeys) {
        if (data[key] !== undefined) clone[key] = data[key];
      }
      if (data.originalAction) {
        clone.originalAction = pruneActionData(data.originalAction, depth + 1);
      }
      return clone;
    };
    const actionForLogging = {
      name: result.actionName,
      // @date_of_change 2025-09-11 - Preserve original spell type for range calculation
      type: resolvedType,
      // @date_of_change 2025-09-15 - Provide extra fields used by CombatLogManager for richer UI
      bonus: actionRequest?.actionData?.bonus ?? actionRequest?.actionData?.attackBonus,
      damage: actionRequest?.actionData?.damage ?? actionRequest?.actionData?.damageFormula,
      desc: actionRequest?.actionData?.desc || actionRequest?.actionData?.description || result?.standardAction?.description || actionRequest?.actionData?.text || '',
      standardAction: result?.standardAction,
      originalAction: pruneActionData(actionRequest?.actionData) || undefined
    };

    // @date_of_change 2025-09-14 - CRITICAL FIX: Transform attack results for proper combat log display
    // CombatLogManager.generateActionDescription expects result.hit and result.damage properties
    let enhancedResult = { ...result };
    const attachTargetHpTransition = (targetResult) => {
      if (!targetResult) return;
      const targetId = targetResult.targetId || targetResult.updatedTarget?.id;
      const originalTarget = targetResult.originalTarget
        || targets.find((target) => target?.id === targetId)
        || targets[0];
      const hpBefore = targetResult.targetHpBefore
        ?? originalTarget?.hpObj?.current
        ?? originalTarget?.hp;
      const hpAfter = targetResult.targetHpAfter
        ?? targetResult.updatedTarget?.hpObj?.current
        ?? targetResult.updatedTarget?.hp;
      const isFiniteHp = (value) => value !== null
        && value !== undefined
        && value !== ''
        && Number.isFinite(Number(value));

      if (isFiniteHp(hpBefore)) enhancedResult.targetHpBefore = Number(hpBefore);
      if (isFiniteHp(hpAfter)) enhancedResult.targetHpRemaining = Number(hpAfter);
    };

    // Handle attack results from executeAttack
    if (result.results && Array.isArray(result.results) && result.results.length > 0) {
      const firstAttackResult = result.results[0];
      if (firstAttackResult.attackRoll && firstAttackResult.attackRoll.isHit !== undefined) {
        const attackRoll = firstAttackResult.attackRoll;
        enhancedResult.hit = firstAttackResult.attackRoll.isHit;
        enhancedResult.attackRoll = {
          ...attackRoll,
          d20Roll: attackRoll.d20Roll ?? attackRoll.roll,
          modifier: attackRoll.modifier ?? attackRoll.attackBonus,
          isCriticalFailure: attackRoll.isCriticalFailure ?? attackRoll.isCriticalFail
        };
        enhancedResult.targetAC = attackRoll.targetAC ?? firstAttackResult.targetAC;
        enhancedResult.damage = firstAttackResult.damage;
        enhancedResult.damageRoll = firstAttackResult.damage;
        enhancedResult.sneakAttack = firstAttackResult.sneakAttack;
        enhancedResult.cunningStrike = firstAttackResult.cunningStrike;
        enhancedResult.uncannyDodge = firstAttackResult.uncannyDodge;
        enhancedResult.hiddenAttack = firstAttackResult.hiddenAttack;
        attachTargetHpTransition(firstAttackResult);
      }
    }

    if (!enhancedResult.attackRoll && result?.result?.results && Array.isArray(result.result.results) && result.result.results.length > 0) {
      const firstNestedResult = result.result.results[0];
      if (firstNestedResult.attackRoll && firstNestedResult.attackRoll.isHit !== undefined) {
        const attackRoll = firstNestedResult.attackRoll;
        enhancedResult.hit = firstNestedResult.attackRoll.isHit;
        enhancedResult.attackRoll = {
          ...attackRoll,
          d20Roll: attackRoll.d20Roll ?? attackRoll.roll,
          modifier: attackRoll.modifier ?? attackRoll.attackBonus,
          isCriticalFailure: attackRoll.isCriticalFailure ?? attackRoll.isCriticalFail
        };
        enhancedResult.targetAC = attackRoll.targetAC ?? firstNestedResult.targetAC;
        enhancedResult.damage = firstNestedResult.damage;
        enhancedResult.damageRoll = firstNestedResult.damage;
        enhancedResult.sneakAttack = firstNestedResult.sneakAttack;
        enhancedResult.cunningStrike = firstNestedResult.cunningStrike;
        enhancedResult.uncannyDodge = firstNestedResult.uncannyDodge;
        enhancedResult.hiddenAttack = firstNestedResult.hiddenAttack;
        attachTargetHpTransition(firstNestedResult);
      }
    }

    const firstResolvedSpellTarget = Array.isArray(result?.result?.affectedTargets)
      ? result.result.affectedTargets.find((targetResult) => targetResult?.updatedTarget)
      : null;
    if (firstResolvedSpellTarget) {
      // date_of_change: 2026-08-20
      // WHAT: Attach the recorded before/after HP transition for resolved spell targets.
      // WHY: The immediate result card reads durable audit fields and must not recalculate HP from
      // damage; spell outcomes previously logged damage while leaving the card on stale target HP.
      attachTargetHpTransition(firstResolvedSpellTarget);
    }

    // @date_of_change: 2025-09-14
    // SPELL LOGGING BRIDGE:
    // If this is a spell and the nested spell result has per-target damage, emit an `effects` array
    // so the combat log can render damage lines like "Deals 7 force damage to Owlbear".
    if (!enhancedResult.effects && result?.result?.affectedTargets?.length) {
      const spellEffects = result.result.affectedTargets
        .filter(t => t?.damage && typeof t.damage.total === 'number')
        .map(t => ({
          type: 'damage',
          target: t.target,
          targetId: t.targetId,
          damage: {
            total: t.damage.total,
            type:
             t.damage.details?.[0]?.damageType ||
             (Array.isArray(result.actionData?.damage) && result.actionData.damage[0]?.type) ||
             result.actionData?.damageType ||
              'force',
            formula: t.damage.formula,
            rolls: t.damage.rolls
          }
        }));
      if (spellEffects.length > 0) {
        enhancedResult.effects = spellEffects;
        enhancedResult.totalDamage = spellEffects.reduce((s, e) => s + (e.damage?.total || 0), 0);
      }
    }

    // @date_of_change: 2025-09-19 - HEALING SPELL LOGGING BRIDGE:
    // If this is a healing spell and the nested spell result has per-target healing, emit an `effects` array
    // so the combat log can render healing lines like "Heals 7 hit points to Paladin".
    // Note: Check result.result.effects first since CombatManager may already provide structured effects
    if (!enhancedResult.effects && result?.result?.effects?.length) {
      // CombatManager already provides structured effects - use them directly
      enhancedResult.effects = result.result.effects;
      enhancedResult.totalDamage = result.result.totalDamage || result.totalDamage || 0;
      enhancedResult.totalHealing = result.result.totalHealing || result.totalHealing || 0;
    } else if (!enhancedResult.effects && result?.result?.affectedTargets?.length) {
      // Fallback: build effects from affectedTargets if CombatManager didn't provide them
      const healingEffects = result.result.affectedTargets
        .filter(t => t?.healing && typeof t.healing.total === 'number')
        .map(t => ({
          type: 'healing',
          target: t.target,
          targetId: t.targetId,
          healing: {
            total: t.healing.total,
            formula: t.healing.formula,
            rolls: t.healing.rolls
          }
        }));
      if (healingEffects.length > 0) {
        enhancedResult.effects = healingEffects;
        enhancedResult.totalHealing = healingEffects.reduce((s, e) => s + (e.healing?.total || 0), 0);
      }
    }

    // @date_of_change 2025-09-17 - SAVING THROW LOGGING: Add detailed saving throw results for combat log
    // For abilities like Fire Breath and spells with saves that use saving throws instead of attack rolls
    // @date_of_change 2025-09-18 - SPELL SAVE FIX: Also check nested spell results (result.result.results)
    // Prefer savingThrow effects over generic per-target damage effects for clarity and consistency.
    {
      // Saved-room and BattleRoom execution wrappers are allowed to carry an
      // empty projection beside the authoritative nested spell result. Choose
      // the first candidate that actually contains a save, then fall back to
      // the affected-target saveResult recorded by CombatManager.
      // date_of_change: 2026-08-20
      const resultCandidates = [
        result?.results,
        result?.result?.results,
        result?.result?.result?.results
      ].filter(Array.isArray);
      const affectedTargetCandidates = [
        result?.affectedTargets,
        result?.result?.affectedTargets,
        result?.result?.result?.affectedTargets
      ].filter(Array.isArray);
      const directSaveResults = resultCandidates.find((entries) => (
        entries.some((entry) => entry?.savingThrow || entry?.saveResult?.savingThrow)
      ));
      const affectedSaveResults = affectedTargetCandidates
        .find((entries) => entries.some((entry) => entry?.savingThrow || entry?.saveResult?.savingThrow));
      const resultsArray = (directSaveResults || affectedSaveResults || []).map((entry) => {
        const recordedSave = entry?.saveResult && typeof entry.saveResult === 'object'
          ? entry.saveResult
          : entry;
        return {
          ...entry,
          savingThrow: entry?.savingThrow || recordedSave?.savingThrow || null,
          saveSuccess: entry?.saveSuccess ?? recordedSave?.saveSuccess,
          damage: entry?.damage || recordedSave?.damage || null,
          target: entry?.target || recordedSave?.target,
          targetId: entry?.targetId || recordedSave?.targetId
        };
      });

      if (resultsArray && resultsArray.length > 0 && resultsArray.some(r => r?.savingThrow)) {
        const savingThrowEffects = resultsArray
          .filter(r => r?.savingThrow || r?.damage)
          .map(r => ({
            type: 'savingThrow',
            target: r.target,
            targetId: r.targetId,
            savingThrow: r.savingThrow ? {
              ability: r.savingThrow.ability,
              roll: r.savingThrow.d20Roll,
              modifier: r.savingThrow.modifier,
              total: r.savingThrow.total,
              dc: r.savingThrow.dc,
              success: r.saveSuccess,
              advantage: r.savingThrow.advantage,
              disadvantage: r.savingThrow.disadvantage
            } : null,
            damage: r.damage ? {
              total: r.damage.total,
              originalTotal: r.damage.originalTotal,
              type: r.damage.type,
              formula: r.damage.formula,
              rolls: r.damage.rolls,
              halfDamage: r.damage.halfDamage,
              resistanceApplied: r.damage.resistanceApplied
            } : null
          }));

        if (savingThrowEffects.length > 0) {
          // Replace any previously created generic effects with the richer saving throw effects
          enhancedResult.effects = savingThrowEffects;
          enhancedResult.totalDamage = savingThrowEffects.reduce((s, e) => s + (e.damage?.total || 0), 0);
          enhancedResult.savingThrowInfo = {
            dc: savingThrowEffects[0]?.savingThrow?.dc,
            ability: savingThrowEffects[0]?.savingThrow?.ability,
            successCount: savingThrowEffects.filter(e => e.savingThrow?.success).length,
            failureCount: savingThrowEffects.filter(e => !e.savingThrow?.success).length
          };

          // DEBUG trace for verification
          console.log('[ActionEconomyProcessor] Created saving throw effects for combat log (preferring over generic effects):', {
            effectsCount: savingThrowEffects.length,
            totalDamage: enhancedResult.totalDamage,
            savingThrowInfo: enhancedResult.savingThrowInfo,
            effects: savingThrowEffects
          });
        }
      }
    }

    // @date_of_change 2025-09-15 - Enrich logs with range, spell, and GM override metadata
    // Include range info from attack validation (feet, normal/long range)
    if (result?.rangeInfo && !enhancedResult.rangeInfo) {
      enhancedResult.rangeInfo = result.rangeInfo;
    }

    // Include spell metadata (slot level used, etc.)
    if ((result?.spellLevel != null || result?.slotUsed != null) && (!enhancedResult.spellMeta)) {
      enhancedResult.spellMeta = {
        level: result.spellLevel,
        slotUsed: result.slotUsed
      };
    }

    // Record GM override flags if present for full audit trail (Tournament Mode transparency)
    if (gmOverride) {
      enhancedResult.gmOverride = {
        note: gmOverride.gmNote,
        ignoreDataValidation: !!gmOverride.ignoreDataValidation,
        ignoreActionEconomy: !!gmOverride.ignoreActionEconomy,
        ignoreHandlerValidation: !!gmOverride.ignoreHandlerValidation,
        customModifiers: gmOverride.customModifiers || {}
      };
    }
    
    // @date_of_change 2025-09-11 - CRITICAL FIX: Add multiattack metadata for proper combat log display
    // Enhance result with multiattack component information if applicable
    if (actionRequest.actionData?.isMultiattackComponent) {
      // Get multiattack sequence info from combat state if available
      const multiattackSequence = combatState?.multiattackSequence || actionRequest.multiattackSequence;
      enhancedResult = {
        ...enhancedResult,
        isComponentAttack: true,
        parentAction: actionRequest.actionData.parentAction || multiattackSequence?.parentAction,
        componentIndex: (multiattackSequence?.currentIndex || 0) + 1,
        totalComponents: multiattackSequence?.componentActions?.length || multiattackSequence?.totalAttacks || 1
      };
    }

    // @date_of_change 2025-09-17 - DEBUG: Trace final combat log call
    console.log('[ActionEconomyProcessor] Calling CombatLogManager.logAction with:', {
      attackerId: attacker.id,
      actionForLogging,
      targetCount: targets.length,
      enhancedResultKeys: Object.keys(enhancedResult),
      hasEffects: !!enhancedResult.effects,
      effectsCount: enhancedResult.effects?.length,
      enhancedResult
    });

    try {
      CombatLogManager.logAction(
        attacker.id,
        actionForLogging,
        targets,
        enhancedResult
      );
      console.log('[ActionEconomyProcessor] CombatLogManager.logAction completed successfully');

      // @date_of_change: 2025-10-09 - Immediate combat log UI refresh hook
      // rule_reference: Separate engine from presentation; emit deterministic audit trail events
      try {
        window.dispatchEvent(new CustomEvent('combat-log-updated', {
          detail: {
            source: 'ActionEconomyProcessor',
            attackerId: attacker?.id,
            actionName: actionForLogging?.name,
            timestamp: Date.now()
          }
        }));
      } catch (evtError) {
        console.warn('[ActionEconomyProcessor] Failed to dispatch combat-log-updated event:', evtError);
      }
    } catch (error) {
      console.error('[ActionEconomyProcessor] Error in CombatLogManager.logAction:', error);
      throw error;
    }

    // Show notification
  }

  /**
   * Create standardized failure result
   */
  static createFailureResult(reason, errors = []) {
    return {
      success: false,
      error: reason,
      errors,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Create standardized error result
   */
  static createErrorResult(error, attacker, actionRequest) {
    console.error('[ActionEconomyProcessor] Action execution error:', error);
    return {
      success: false,
      error: error.message,
      stack: error.stack,
      attacker: { id: attacker?.id, name: attacker?.name },
      actionRequest,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * GM Override Helper - Create override object for bypassing validation
   */
  static createGMOverride(options = {}) {
    return {
      timestamp: new Date().toISOString(),
      gmNote: options.note || 'GM Override Applied',
      ignoreDataValidation: options.ignoreDataValidation || false,
      ignoreActionEconomy: options.ignoreActionEconomy || false,
      ignoreHandlerValidation: options.ignoreHandlerValidation || false,
      customModifiers: options.customModifiers || {},
      ...options
    };
  }
}

export default ActionEconomyProcessor;
